import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const translations = {
  en: {
    sheba: { badge: 'AUTHORIZED REPRESENTATIVES OF SHEBA' },
    livechat: {
      tooltip: 'Live Chat',
      title: 'Live Chat',
      subtitle: 'Send us a message — we respond quickly.',
      name: 'Name',
      phone: 'Phone',
      message: 'Your message...',
      send: 'Send Message',
      hint: 'Prefer instant chat? Use Messenger, WhatsApp or Viber icons on the left.',
      greeting: 'Hello! Welcome to J.House of Secretary. How can we help you today?',
      callUs: 'Call Us',
      office: 'Office',
      mobile: 'Mobile',
      followUs: 'Follow',
      website: 'Website',
      qrHint: 'Scan with your phone camera to open our page',
      openPage: 'Open Page',
    },
    splash: {
      back: 'Back',
      tagline: 'Healthcare Coordination • International Access • Professional Support',
      forDoctors: 'For Doctors Clinics Hospitals',
      forPatients: 'For Patients',
      internationalAccess: 'International Health Access',
      trustedBy: 'TRUSTED BY',
      nightPharmacies: 'NIGHT PHARMACIES',
      nightPharmaciesDesc: 'Find the nearest night pharmacy in your area',
      nightPharmaciesFooter: 'Data is updated daily.',
      disclaimer1: 'The "Patient Line" provides facilitation and patient service for finding available doctors and scheduling appointments.',
      disclaimer2: 'The service may suggest available partner doctors based on availability and patient needs, without affecting medical decisions.',
      disclaimer3: 'The final choice of doctor is made exclusively by the patient.',
      disclaimer4: 'Partner doctors participate through an annual subscription for network participation and request management, not linked to case numbers or referrals.',
      disclaimer5: 'The service does not provide medical advice nor substitute the doctor-patient relationship.',
    },
    philosophy: {
      eyebrow: 'OUR PHILOSOPHY',
      etymology: 'The Greek word «ασθενής» (patient) comes from the privative «α-» and «σθένος» (strength) — denoting one who needs support, care and renewed strength.',
      paragraphs: [
        'We believe that every interaction with a healthcare professional is an opportunity to restore a little of that strength. That is why we place such emphasis on the very first call — where courtesy, understanding and professionalism can make all the difference.',
        'At J.House of Secretary, we do not simply manage calls and appointments. We support people at moments when they are seeking guidance, care and access to the right healthcare services.',
        'Through our secretarial support services, the Patient Line and International Health Access, we ensure that every person feels heard, understood and guided with respect and responsibility.',
        'From the first phone call to connecting with specialised healthcare professionals in Cyprus and abroad, our mission is to transform uncertainty into clear guidance, obstacles into solutions, and anxiety into a feeling of confidence.',
      ],
      closing: 'Because behind every call there is not merely a patient, but a human being who deserves to feel that they are not alone on the journey towards health.',
    },
    trust: {
      online: 'Visitors Online',
      calls: 'Calls Handled',
      coverage: 'Continuous Coverage',
      languages: 'Multilingual Support',
      satisfaction: 'Customer Satisfaction',
      languagesWord: 'languages',
      reviewsWord: 'reviews',
      badgeOnline: 'Now',
      badgeLive: 'Live',
      badgeAlwaysOn: 'Always On',
      badgeMultilingual: 'Multilingual',
      badgeExcellence: 'Excellence',
    },
    nav: {
      home: 'Home',
      services: 'Services',
      whyUs: 'Why Us',
      testimonials: 'Testimonials',
      faq: 'FAQ',
      contact: 'Contact',
      pricing: 'Pricing',
    },
    hero: {
      tagline: 'Your Trusted Partner in Professional Secretarial Services',
      headline: 'Never Miss a Patient Call Again',
      title: 'Welcome to',
      subtitle: 'J.House of Secretary',
      description: 'Your trusted partner in professional, corporate and medical telephone secretarial services, dedicated to enhancing your business efficiency.',
      cta: 'Contact Us',
      ctaSecondary: 'Explore Services',
      ctaConsultation: 'Book a Free Consultation',
      available: '24/7 Available',
    },
    heroFeatures: {
      callAnswering: '24/7 Call Answering',
      appointmentReminders: 'Appointment Reminders',
      patientFollowUp: 'Patient Follow-up',
      telemedicineScheduling: 'Telemedicine Scheduling',
      patientIntake: 'Patient Intake',
      clinicWorkflow: 'Clinic Workflow Support',
    },
    valueProposition: {
      headline: 'Organize your practice effortlessly – Professional secretarial support, fully compliant with GESY & GDPR',
      subheadline: 'Save time, reduce errors, and cut costs without hiring a permanent secretary.',
      ctaDemo: 'Book a free 10-minute demo',
      benefits: {
        callManagement: 'Phone call & appointment management – Professional support without delays.',
        gesyReferrals: 'GESY referral verification & registration – Safe, accurate, and fully compliant process.',
        costSaving: 'Cost & time savings – Complete secretarial support at significantly lower cost than hiring a permanent secretary.',
        compliance: 'Compliance & data security – Fully aligned with legislation and GDPR.',
        packages: 'Customized service packages – Basic, Professional, or Premium, according to your needs.',
      },
    },
    services: {
      title: 'Comprehensive Telephone Secretarial Services',
      subtitle: 'Streamline your operations with the support of our specialized telephone secretaries.',
      corporate: {
        title: 'Corporate Telesecretary Support',
        description: 'Our corporate telesecretary services include call management, appointment scheduling, and administrative support, allowing businesses to focus on their core activities while we efficiently handle their communication needs.',
      },
      medical: {
        title: 'Medical Telesecretary Services',
        description: 'Designed for healthcare professionals, our medical telesecretary services ensure patient calls are handled with care, appointments are managed efficiently, and medical documentation remains updated, enhancing patient satisfaction.',
      },
      virtual: {
        title: 'Virtual Assistant Services',
        description: 'Our virtual assistant services provide flexible and reliable support, from email management to research. We offer the perfect solution for individuals looking to maximize their productivity.',
      },
      pricing: {
        title: 'Pricing',
        description: 'Prices according to your call volume.',
      },
      gesy: {
        title: 'GESY',
        description: 'We collaborate with: Private medical clinics, Diagnostic centers, Healthcare professionals, Major medical organizations in Cyprus. Our services support medical teams in managing patient communication, appointments, and GESY referral processes efficiently.',
      },
    },
    detailedServices: {
      title: 'Our Services',
      intro: 'At J House of Secretary, we provide specialized medical secretarial support services for private practices, clinics, and medical centers. Our goal is to help healthcare professionals better organize their daily practice operations, reducing administrative burden and improving patient service.',
      phoneCalls: {
        title: 'Patient Call Management',
        items: ['Answering incoming calls', 'Recording patient messages', 'Forwarding important calls to the doctor', 'Managing patient inquiries', 'Informing patients about operating hours']
      },
      appointments: {
        title: 'Appointment Management',
        items: ['Booking new appointments', 'Changing and canceling appointments', 'Organizing doctor\'s calendar', 'Avoiding double bookings', 'Appointment reminders']
      },
      emailManagement: {
        title: 'Practice Email Management',
        items: ['Monitoring incoming emails', 'Responding to basic patient requests', 'Organizing professional correspondence', 'Forwarding important messages to the doctor']
      },
      patientComm: {
        title: 'Patient Communication',
        items: ['Informing patients about appointments', 'Follow-up after visits', 'Answering basic patient questions', 'Managing new patient requests']
      },
      patientIntake: {
        title: 'Patient Intake',
        items: ['Recording new patient details', 'Collecting basic information before appointments', 'Organizing patient forms']
      },
      adminSupport: {
        title: 'Practice Administrative Support',
        items: ['Organizing patient files', 'Document management', 'Report preparation', 'Daily administrative task support']
      },
      telemedicine: {
        title: 'Telemedicine Support',
        items: ['Organizing online consultations', 'Sending links for online appointments', 'Helping patients with technical issues']
      },
      followUp: {
        title: 'Patient Follow-up',
        items: ['Re-examination reminders', 'Post-treatment follow-up', 'Information about next appointments']
      },
      clinicComm: {
        title: 'Clinic Communication Management',
        items: ['Coordination between doctors and patients', 'Organizing daily schedule', 'Managing collaboration requests']
      },
      newPatients: {
        title: 'New Patient Support',
        items: ['Information about practice services', 'Guidance for first visit', 'Basic exam information']
      },
      medicalCenters: {
        title: 'Small Medical Center Support',
        items: ['Coordination between departments', 'Organizing daily operations', 'Patient management']
      },
      remoteSupport: {
        title: 'Remote Secretarial Support',
        items: ['Remote secretary services', 'Flexible collaboration', 'Staff cost reduction']
      },
      gesyReferrals: {
        title: 'GESY Referral Management',
        items: ['Checking referral validity', 'Tracking approvals and notifying about any issues', 'Reports for you or your staff', 'Save time and ensure all referrals are correct and ready']
      }
    },
    gdprSection: {
      title: 'Compliance with legislation & data protection',
      description: 'Our services are fully compliant with GDPR and GESY legislation, ensuring that all patient data is handled securely and confidentially.',
    },
    whyUs: {
      title: 'Why Partner with J.House of Secretary?',
      subtitle: 'Discover the unique advantages of working with us for your telephone secretarial needs.',
      expertise: {
        title: 'Professional Expertise',
        description: 'Our team consists of experienced professionals with extensive experience in telephone secretarial services, ensuring you receive the highest quality support.',
      },
      personalized: {
        title: 'Personalized Approaches',
        description: 'We pride ourselves on our ability to customize our services to meet the specific needs of our clients, setting us apart from the standard solutions offered by competitors.',
      },
      commitment: {
        title: 'Commitment to Excellence',
        description: 'Our unwavering commitment to excellence and customer satisfaction means we go above and beyond to ensure our clients receive the best possible experience.',
      },
    },
    facilities: {
      title: 'Our Modern Facilities',
      subtitle: 'Discover our state-of-the-art office space designed for professional telesecretary services.',
    },
    testimonials: {
      title: 'What Our Clients Say',
      subtitle: 'Hear from businesses who trust us with their communication needs.',
      items: [
        {
          name: 'Dr. Andreas K.',
          role: 'Medical Clinic Director',
          text: 'J.House of Secretary has transformed how we handle patient calls. Professional, efficient, and always available when we need them.',
        },
        {
          name: 'Maria S.',
          role: 'Business Owner',
          text: 'The corporate telesecretary service has been invaluable. Our clients always receive professional attention, even when we\'re unavailable.',
        },
        {
          name: 'Christos P.',
          role: 'Legal Firm Partner',
          text: 'Outstanding service. The multilingual support is exceptional and has helped us expand our international client base.',
        },
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to common questions about our services.',
      items: [
        {
          question: 'What services do you offer?',
          answer: 'We offer comprehensive telephone secretarial services including corporate telesecretary support, medical telesecretary services, and virtual assistant services tailored to your needs.',
        },
        {
          question: 'Are your services available 24/7?',
          answer: 'Yes, our dedicated customer service team is available 24 hours a day to ensure all your telesecretary needs are met promptly and efficiently.',
        },
        {
          question: 'What languages do you support?',
          answer: 'We offer multilingual support in Greek, English, Russian, Arabic, Mandarin, French, and Spanish to serve our diverse international clientele.',
        },
        {
          question: 'How is pricing determined?',
          answer: 'Pricing is based on your call volume and specific requirements. Contact us for a personalized quote tailored to your business needs.',
        },
        {
          question: 'How do I get started?',
          answer: 'Simply contact us via phone or email, and our team will guide you through the onboarding process and customize a service plan for you.',
        },
      ],
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Get in touch with our team for a personalized consultation.',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        phone: 'Phone Number',
        message: 'Your Message',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Message sent successfully!',
        error: 'Failed to send message. Please try again.',
      },
      info: {
        phone: 'Phone',
        mobile: 'Mobile',
        email: 'Email',
        address: 'Address',
        addressLine: 'Afroditis 10, Nicosia 1060, Cyprus',
      },
    },
    chat: {
      title: 'Live Chat',
      placeholder: 'Type your message...',
      welcome: 'Hello! How can we help you today?',
      offline: 'We\'re currently offline. Please leave a message.',
    },
    footer: {
      rights: 'All Rights Reserved',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
    partners: {
      title: 'Our Partners',
      subtitle: 'Trusted by leading healthcare institutions in Cyprus',
    },
    patientLine: {
      back: 'Back',
      phone: '7000 0223',
      tagline: 'The New Innovative Service from J.House of Secretary',
      title: 'The Patient Line',
      slogan: '"Your health, one step ahead."',
      free: 'Free for all patients — no charge.',
      mainMessage: 'End of patient hassle.',
      subMessage: 'You call — we handle it.',
      servicesTitle: 'What we handle for you',
      services: [
        'Booking appointments with the specialty you need',
        'Access to radiology & hematology centers',
        'Finding the nearest pharmacy',
        'Appointment reminders',
        'Follow-up after examination'
      ],
      stepsTitle: 'Steps for Patients',
      stepsSubtitle: 'Just 4 steps to solve any health need',
      steps: [
        { number: '01', title: 'Call the Patient Line', description: 'Call 7000 0223 / 801 101 0364 and speak with our specialized team.' },
        { number: '02', title: 'Tell us what you need', description: 'Appointment with specialty, examination, access to diagnostic center or pharmacy.' },
        { number: '03', title: 'We handle everything', description: 'We book appointments, check referrals, find the nearest service point.' },
        { number: '04', title: 'You receive confirmation', description: 'We inform you about the date, time and location of your appointment.' }
      ],
      benefitsTitle: 'End of Stress',
      benefitsSubtitle: 'Why choose the Patient Line',
      benefits: [
        { title: 'One phone for everything', description: 'Call only 7000 0223 / 801 101 0364 for every health need.' },
        { title: 'Time saving', description: 'No more waiting and endless calls.' },
        { title: '100% Free', description: 'No charge for patients.' },
        { title: 'Personal care', description: 'Personalized service for each patient.' },
        { title: 'GDPR Compliant', description: 'Absolute protection of your personal data.' }
      ],
      ctaTitle: 'Call now',
      ctaSubtitle: 'Available daily to serve you',
      compatible: 'Compatible with',
    },
    international: {
      back: 'Back',
      pageTitle: 'International Health Access',
      subtitle: 'Coordination for patients seeking specialized solutions abroad',
      description: 'We undertake the professional coordination of patients seeking advanced medical services, second opinions, or treatment at recognized international health centers. With absolute discretion, proper guidance, and personalized support at every stage.',
      missionQuote: '"Helping healthcare work better — Specialized care solutions beyond borders"',
      servicesTitle: 'International Coordination Services',
      service1Title: 'Medical case coordination abroad',
      service1Desc: 'We fully coordinate treatment at recognized international centers',
      service2Title: 'Second medical opinion',
      service2Desc: 'Access to specialized doctors from top institutions',
      service3Title: 'Medical file & communication support',
      service3Desc: 'Full document management, translations, and correspondence',
      service4Title: 'Appointment & procedure organization',
      service4Desc: 'Appointment booking, exam preparation, logistics',
      service5Title: 'Personalized patient guidance',
      service5Desc: 'Individualized support at every stage of the process',
      service6Title: 'Premium Patient Assistance',
      service6Desc: 'Concierge services for VIP patients with absolute discretion',
      whoTitle: 'Who Is It For',
      patients: 'Patients',
      patientsDesc: 'We help you organize safe access to specialized health centers, with full support from Cyprus to your arrival',
      doctorsTitle: 'Doctors & Clinics',
      doctorsDesc: 'We handle administrative and international coordination of complex cases so the patient is properly served',
      hospitalsTitle: 'Hospitals & Institutions',
      hospitalsDesc: 'Through international partnerships with recognized health organizations, we provide access to specialized services',
      ctaTitle: 'Need Specialized Treatment Abroad?',
      ctaDesc: 'Fast specialist access through our Cyprus coordination office',
      ctaConsultation: 'Request Consultation',
      ctaUpload: 'Upload Medical Reports',
      ctaCall: 'Speak With Coordinator',
      faqTitle: 'Frequently Asked Questions',
      faqSubtitle: 'International Health Access',
      featuredPartnerLabel: 'Featured Partner',
      holisticTagline: 'World-Class Medical Care in Israel',
      holisticDescription: 'Ranked among the world\u2019s top hospitals by Newsweek, Sheba Medical Center at Tel HaShomer is internationally recognised for excellence in oncology, cardiology, transplantation, fertility, neurology and advanced surgical care. Through Sheba Global and in coordination with J.House of Secretary, international patients gain access to leading physicians, pioneering research and fully personalised, multilingual case management — from the first enquiry through to post-treatment follow-up.',
      holisticTags: ['Oncology', 'Cardiology', 'Transplantation', 'International Patients'],
      uploadModal: {
        title: 'Upload Medical Documents',
        subtitle: 'Send your medical reports and files securely',
        nameLabel: 'Full Name',
        phoneLabel: 'Phone',
        emailLabel: 'Email',
        messageLabel: 'Message (optional)',
        messagePlaceholder: 'Briefly describe your case...',
        filesLabel: 'Files / Folders',
        filesHint: 'All file types accepted • Max 25MB total',
        selectFiles: 'Select Files',
        selectFolder: 'Select Folder',
        clearFiles: 'Clear',
        gdprConsent: 'I consent to the processing of my data per GDPR for the purpose of this request.',
        submitBtn: 'Send',
        cancelBtn: 'Cancel',
        sending: 'Sending...',
        successMsg: 'Your request has been sent successfully. We will contact you shortly.',
        errorMsg: 'Failed to send. Please try again or contact us directly.',
        sizeError: 'Total file size exceeds 25MB limit.',
        requiredError: 'Please fill in all required fields.',
        gdprError: 'Please accept the GDPR consent to continue.',
        rateLimitError: 'Too many requests. Please try again in {minutes} minute(s).',
        filesSelected: 'file(s) selected',
      },
      faqItems: [
        { q: 'What is the International Health Access service?', a: 'It is a professional coordination service for patients seeking a second opinion, specialized treatment, or access to recognized international health centers.' },
        { q: 'Do you provide medical advice?', a: 'No. We do not provide medical diagnosis or treatment. We handle administrative and organizational coordination between patients and international healthcare providers.' },
        { q: 'How does the process start?', a: 'Simply contact us and send basic information or available test results so the most appropriate coordination path can be evaluated.' },
        { q: 'Do I need to travel abroad?', a: 'Not always. In many cases, a second opinion or remote evaluation can be arranged initially.' },
        { q: 'Can I send my tests electronically?', a: 'Yes. You can send tests, reports, and medical documents electronically in a secure manner.' },
        { q: 'Which countries can you help with?', a: 'Depending on the case and partner organization availability, we provide access to selected international health centers.' },
        { q: 'How quickly can a request proceed?', a: 'Time varies by case, but we prioritize urgent and serious cases with immediate coordination.' },
        { q: 'Do you provide services in Greek?', a: 'Yes. We provide full support in Greek and, where needed, in English.' },
        { q: 'What types of cases do you handle?', a: 'We support cases such as second opinions, complex cases, specialized treatments, oncology, cardiology, pediatrics, neurology, and more.' },
        { q: 'Do you handle appointments?', a: 'Yes. We can help schedule appointments, procedures, and related steps.' },
        { q: 'Do you provide support during treatment?', a: 'Yes. We remain available for administrative coordination and guidance at every stage.' },
        { q: 'What about the confidentiality of my personal data?', a: 'All information is handled with professionalism, discretion, and respect for privacy.' },
        { q: 'How much does the service cost?', a: 'The cost depends on the type of request and coordination requirements. Information is provided before starting.' },
        { q: 'Can I request only a second opinion?', a: 'Of course. Many patients initially request a second opinion before deciding on next steps.' },
        { q: 'Do you work with doctors and clinics?', a: 'Yes. We also support healthcare professionals, clinics, and organizations that need international case coordination.' },
        { q: 'Do you serve all of Cyprus?', a: 'Yes. We provide island-wide service with remote communication capability.' },
        { q: 'Can I contact you via WhatsApp?', a: 'Yes. Direct communication is available via phone and WhatsApp.' },
        { q: 'What is your role in the process?', a: 'Our role is to simplify a complex process and act as a coordination point between patients and international health bodies.' },
        { q: 'What about emergency cases?', a: 'You can contact us immediately so the fastest possible way of support can be evaluated.' },
        { q: 'How can I start today?', a: 'You can contact us by phone, WhatsApp, or through the contact form on our website.' },
      ],
      footer: 'Healthcare Coordination • International Access • Professional Support',
    },
    cookieConsent: {
      title: 'We use cookies',
      message: 'This website uses cookies to ensure you get the best experience. We use only necessary cookies for essential functionality and analytics to improve our services.',
      learnMore: 'Learn more',
      acceptAll: 'Accept All',
      necessaryOnly: 'Necessary Only',
      policyTitle: 'Cookie Policy',
      section1Title: 'What are cookies?',
      section1Body: 'Cookies are small text files stored on your device when you visit a website. They help the site function properly and provide a better browsing experience.',
      section2Title: 'How we use cookies',
      section2Body: 'We use cookies for essential site functionality (login sessions, language preferences) and to understand how visitors interact with our website.',
      section3Title: 'Types of cookies we use',
      cookieType1: 'Essential cookies — required for the site to function (login, language, session)',
      cookieType2: 'Functional cookies — remember your preferences (theme, settings)',
      cookieType3: 'Analytics cookies — help us understand site usage (anonymous)',
      section4Title: 'Your rights (GDPR)',
      section4Body: 'You can change or withdraw your consent at any time by clearing your browser data. You also have the right to access, correct, or delete your personal data per the EU General Data Protection Regulation.',
      contactTitle: 'Contact us',
    },
  },
  el: {
    sheba: { badge: 'ΕΞΟΥΣΙΟΔΟΤΗΜΕΝΟΙ ΑΝΤΙΠΡΟΣΩΠΟΙ ΤΟΥ SHEBA' },
    livechat: {
      tooltip: 'Ζωντανή Συνομιλία',
      title: 'Ζωντανή Συνομιλία',
      subtitle: 'Στείλτε μας μήνυμα — απαντάμε άμεσα.',
      connected: 'Συνδεθήκατε — θα σας απαντήσουμε σύντομα.',
      name: 'Ονοματεπώνυμο',
      phone: 'Τηλέφωνο',
      message: 'Το μήνυμά σας...',
      placeholder: 'Γράψτε ένα μήνυμα...',
      send: 'Αποστολή Μηνύματος',
      sending: 'Αποστολή...',
      end: 'Τερματισμός συνομιλίας',
      error: 'Η αποστολή απέτυχε. Δοκιμάστε ξανά.',
      hint: 'Προτιμάτε στιγμιαία επικοινωνία; Χρησιμοποιήστε τα εικονίδια Messenger, WhatsApp ή Viber αριστερά.',
      greeting: 'Καλωσορίσατε στην J.House of Secretary! Πώς μπορούμε να σας βοηθήσουμε;',
      callUs: 'Καλέστε μας',
      office: 'Γραφείο',
      mobile: 'Κινητό',
      followUs: 'Ακολουθήστε',
      website: 'Ιστοσελίδα',
      qrHint: 'Σαρώστε με την κάμερα του κινητού σας για να ανοίξει η σελίδα μας',
      openPage: 'Άνοιγμα Σελίδας',
    },
    splash: {
      back: 'Πίσω',
      tagline: 'Συντονισμός Υγειονομικής Περίθαλψης • Διεθνής Πρόσβαση • Επαγγελματική Υποστήριξη',
      forDoctors: 'Για Γιατρούς Κλινικές Νοσοκομεία',
      forPatients: 'Για Ασθενείς',
      internationalAccess: 'Διεθνής Πρόσβαση Υγείας',
      trustedBy: 'ΜΑΣ ΕΜΠΙΣΤΕΥΟΝΤΑΙ',
      nightPharmacies: 'ΔΙΑΝΥΚΤΕΡΕΥΟΝΤΑ ΦΑΡΜΑΚΕΙΑ',
      nightPharmaciesDesc: 'Βρείτε το πλησιέστερο διανυκτερεύον φαρμακείο στην περιοχή σας',
      nightPharmaciesFooter: 'Τα στοιχεία ενημερώνονται καθημερινά.',
      disclaimer1: 'Η «Γραμμή του Ασθενή» παρέχει υπηρεσίες διευκόλυνσης και εξυπηρέτησης ασθενών για την εύρεση διαθέσιμων ιατρών και τον προγραμματισμό ραντεβού.',
      disclaimer2: 'Η υπηρεσία δύναται να προτείνει διαθέσιμους συνεργαζόμενους ιατρούς βάσει διαθεσιμότητας και αναγκών του ασθενή, χωρίς να επηρεάζει ιατρικές αποφάσεις.',
      disclaimer3: 'Η τελική επιλογή ιατρού πραγματοποιείται αποκλειστικά από τον ασθενή.',
      disclaimer4: 'Οι συνεργαζόμενοι ιατροί συμμετέχουν μέσω ετήσιας συνδρομής, η οποία αφορά τη συμμετοχή στο δίκτυο και τη διαχείριση αιτημάτων και δεν συνδέεται με αριθμό ή προώθηση περιστατικών.',
      disclaimer5: 'Η υπηρεσία δεν παρέχει ιατρικές συμβουλές ούτε υποκαθιστά τη σχέση ιατρού–ασθενούς.',
    },
    trust: {
      online: 'Επισκέπτες Online',
      calls: 'Διαχειριζόμενες Κλήσεις',
      coverage: 'Συνεχής Κάλυψη',
      languages: 'Πολυγλωσσική Υποστήριξη',
      satisfaction: 'Ικανοποίηση Πελατών',
      languagesWord: 'γλώσσες',
      reviewsWord: 'αξιολογήσεις',
      badgeOnline: 'Τώρα',
      badgeLive: 'Ζωντανά',
      badgeAlwaysOn: 'Πάντα Διαθέσιμοι',
      badgeMultilingual: 'Πολυγλωσσική',
      badgeExcellence: 'Αριστεία',
    },
    philosophy: {
      eyebrow: 'Η ΦΙΛΟΣΟΦΙΑ ΜΑΣ',
      etymology: 'Η λέξη «ασθενής» προέρχεται από το α- στερητικό και το σθένος, δηλώνοντας εκείνον που έχει ανάγκη υποστήριξης, φροντίδας και ενδυνάμωσης.',
      paragraphs: [
        'Πιστεύουμε ότι κάθε επικοινωνία με έναν επαγγελματία υγείας είναι μια ευκαιρία να προσφέρουμε ξανά λίγη από αυτή τη δύναμη. Γι\u2019 αυτό δίνουμε ιδιαίτερη σημασία στην πρώτη κλήση, όπου η ευγένεια, η κατανόηση και ο επαγγελματισμός μπορούν να κάνουν τη διαφορά.',
        'Στην J.House of Secretary δεν διαχειριζόμαστε απλώς κλήσεις και ραντεβού. Υποστηρίζουμε ανθρώπους σε στιγμές που αναζητούν καθοδήγηση, φροντίδα και πρόσβαση στις κατάλληλες υπηρεσίες υγείας.',
        'Μέσα από τις υπηρεσίες γραμματειακής υποστήριξης, τη Γραμμή Ασθενή και τη Διεθνή Πρόσβαση Υγείας, φροντίζουμε ώστε κάθε άνθρωπος να αισθάνεται ότι ακούγεται, κατανοείται και καθοδηγείται με σεβασμό και υπευθυνότητα.',
        'Από την πρώτη τηλεφωνική επικοινωνία έως τη σύνδεση με εξειδικευμένους επαγγελματίες υγείας στην Κύπρο και το εξωτερικό, αποστολή μας είναι να μετατρέπουμε την αβεβαιότητα σε σαφή καθοδήγηση, τα εμπόδια σε λύσεις και το άγχος σε αίσθημα σιγουριάς.',
      ],
      closing: 'Γιατί πίσω από κάθε κλήση δεν βρίσκεται απλώς ένας ασθενής, αλλά ένας άνθρωπος που αξίζει να νιώθει ότι δεν είναι μόνος στη διαδρομή προς την υγεία.',
    },
    nav: {
      home: 'Αρχική',
      services: 'Υπηρεσίες',
      whyUs: 'Γιατί Εμάς',
      testimonials: 'Μαρτυρίες',
      faq: 'Συχνές Ερωτήσεις',
      contact: 'Επικοινωνία',
      pricing: 'Τιμοκατάλογος',
    },
    hero: {
      tagline: 'Ο Έμπιστος Συνεργάτης σας σε Επαγγελματικές Γραμματειακές Υπηρεσίες',
      headline: 'Μην Χάσετε ΠΟΤΕ Ξανά Κλήση Ασθενούς',
      title: 'Καλώς Ήρθατε στην',
      subtitle: 'J.House of Secretary',
      description: 'Ο έμπιστος συνεργάτης σας σε επαγγελματικές, εταιρικές και ιατρικές τηλεγραμματειακές υπηρεσίες, αφοσιωμένοι στην ενίσχυση της επιχειρησιακής σας αποτελεσματικότητας.',
      cta: 'Επικοινωνήστε Μαζί Μας',
      ctaSecondary: 'Εξερευνήστε Υπηρεσίες',
      ctaConsultation: 'Κλείστε μια Δωρεάν Ενημερωτική Συνάντηση',
      available: '24/7 ΔΙΑΘΕΣΙΜΟΤΗΤΑ',
    },
    heroFeatures: {
      callAnswering: 'Απαντήσεις κλήσεων 24/7',
      appointmentReminders: 'Υπενθυμίσεις ραντεβού',
      patientFollowUp: 'Παρακολούθηση ασθενών',
      telemedicineScheduling: 'Προγραμματισμός τηλεϊατρικής',
      patientIntake: 'Εισαγωγή ασθενών',
      clinicWorkflow: 'Υποστήριξη ροής εργασίας κλινικής',
    },
    valueProposition: {
      headline: 'Οργανώστε το ιατρείο σας χωρίς κόπο – Επαγγελματική γραμματειακή υποστήριξη, πλήρως εναρμονισμένη με ΓΕΣΥ & GDPR',
      subheadline: 'Εξοικονομήστε χρόνο, μειώστε λάθη και μειώστε το κόστος χωρίς να προσλάβετε μόνιμο γραμματέα.',
      ctaDemo: 'Κλείστε δωρεάν demo 10 λεπτών',
      benefits: {
        callManagement: 'Διαχείριση τηλεφωνικών κλήσεων & ραντεβού – Επαγγελματική υποστήριξη χωρίς καθυστερήσεις.',
        gesyReferrals: 'Έλεγχος παραπεμπτικών ΓΕΣΥ – Ασφαλής, σωστή και πλήρως εναρμονισμένη διαδικασία.',
        costSaving: 'Εξοικονόμηση κόστους & χρόνου – Πλήρης γραμματειακή υποστήριξη με σημαντικά χαμηλότερο κόστος από την πρόσληψη μόνιμου γραμματέα.',
        compliance: 'Compliance & ασφάλεια δεδομένων – Πλήρως εναρμονισμένο με τη νομοθεσία και τον GDPR.',
        packages: 'Προσαρμοσμένα πακέτα υπηρεσιών – Basic, Professional ή Premium, ανάλογα με τις ανάγκες σας.',
      },
    },
    services: {
      title: 'Εκτενείς Τηλεφωνικές Γραμματειακές Υπηρεσίες',
      subtitle: 'Επιταχύνετε τις διαδικασίες σας με την υποστήριξη των εξειδικευμένων τηλεφωνικών γραμματέων μας.',
      corporate: {
        title: 'Εταιρική Τηλε-Γραμματειακή Υποστήριξη',
        description: 'Οι εταιρικές τηλε-γραμματειακές μας υπηρεσίες περιλαμβάνουν τη διαχείριση κλήσεων, τον προγραμματισμό ραντεβού και τη διοικητική υποστήριξη, επιτρέποντας στις επιχειρήσεις να επικεντρωθούν στις βασικές τους δραστηριότητες.',
      },
      medical: {
        title: 'Ιατρικές Τηλε-Γραμματειακές Υπηρεσίες',
        description: 'Σχεδιασμένες για επαγγελματίες υγείας, οι ιατρικές τηλε-γραμματειακές μας υπηρεσίες εξασφαλίζουν ότι οι κλήσεις των ασθενών χειρίζονται με φροντίδα και τα ραντεβού διαχειρίζονται αποτελεσματικά.',
      },
      virtual: {
        title: 'Υπηρεσίες Εικονικού Βοηθού',
        description: 'Οι υπηρεσίες εικονικού βοηθού μας παρέχουν ευέλικτη και αξιόπιστη υποστήριξη, από τη διαχείριση email έως τη διεξαγωγή έρευνας.',
      },
      pricing: {
        title: 'Τιμοκατάλογος',
        description: 'Η τιμολόγηση μας προσαρμόζεται στις ανάγκες του ιατρείου σας, με βάση τον όγκο κλήσεων, τις υπηρεσίες και τις ειδικές απαιτήσεις. Προσφέρουμε προσωποποιημένη και διαφανή προσφορά. Προσφέρουμε προσαρμοσμένα πακέτα υπηρεσιών (Basic, Professional, Premium).',
      },
      gesy: {
        title: 'ΓΕΣΥ',
        description: 'Συνεργαζόμαστε με: Ιδιωτικές ιατρικές κλινικές, Διαγνωστικά κέντρα, Επαγγελματίες υγείας, Μεγάλους ιατρικούς φορείς στην Κύπρο. Οι υπηρεσίες μας υποστηρίζουν τις ιατρικές ομάδες στη διαχείριση της επικοινωνίας με τους ασθενείς, των ραντεβού και των διαδικασιών παραπομπής στο ΓΕΣΥ με αποδοτικό τρόπο.',
      },
    },
    detailedServices: {
      title: 'Οι Υπηρεσίες μας',
      intro: 'Στην J House of Secretary παρέχουμε εξειδικευμένες υπηρεσίες ιατρικής γραμματειακής υποστήριξης για ιδιωτικά ιατρεία, κλινικές και ιατρικά κέντρα. Στόχος μας είναι να βοηθήσουμε τους επαγγελματίες υγείας να οργανώσουν καλύτερα την καθημερινή λειτουργία του ιατρείου τους, μειώνοντας τον διοικητικό φόρτο και βελτιώνοντας την εξυπηρέτηση των ασθενών.',
      phoneCalls: {
        title: 'Διαχείριση τηλεφωνημάτων ασθενών',
        items: ['Απάντηση εισερχόμενων κλήσεων', 'Καταγραφή μηνυμάτων ασθενών', 'Προώθηση σημαντικών κλήσεων στον ιατρό', 'Διαχείριση ερωτήσεων ασθενών', 'Ενημέρωση ασθενών για ώρες λειτουργίας']
      },
      appointments: {
        title: 'Διαχείριση ραντεβού',
        items: ['Κλείσιμο νέων ραντεβού', 'Αλλαγή και ακύρωση ραντεβού', 'Οργάνωση ημερολογίου ιατρού', 'Αποφυγή διπλοκρατήσεων', 'Υπενθυμίσεις ραντεβού']
      },
      emailManagement: {
        title: 'Διαχείριση email ιατρείου',
        items: ['Παρακολούθηση εισερχόμενων email', 'Απάντηση σε βασικά αιτήματα ασθενών', 'Οργάνωση επαγγελματικής αλληλογραφίας', 'Προώθηση σημαντικών μηνυμάτων στον ιατρό']
      },
      patientComm: {
        title: 'Επικοινωνία με τον ασθενή',
        items: ['Ενημέρωση ασθενών για ραντεβού', 'Follow-up μετά από επίσκεψη', 'Απάντηση σε βασικές απορίες ασθενών', 'Διαχείριση αιτημάτων νέων ασθενών']
      },
      patientIntake: {
        title: 'Υποδοχή νέων ασθενών',
        items: ['Καταγραφή στοιχείων νέων ασθενών', 'Συλλογή βασικών πληροφοριών πριν το ραντεβού', 'Οργάνωση εντύπων ασθενών']
      },
      adminSupport: {
        title: 'Διοικητική υποστήριξη ιατρείου',
        items: ['Οργάνωση αρχείων ασθενών', 'Διαχείριση εγγράφων', 'Προετοιμασία αναφορών', 'Υποστήριξη καθημερινών διοικητικών εργασιών']
      },
      telemedicine: {
        title: 'Υποστήριξη τηλεϊατρικής',
        items: ['Οργάνωση online consultations', 'Αποστολή link για online ραντεβού', 'Βοήθεια ασθενών με τεχνικά θέματα']
      },
      followUp: {
        title: 'Παρακολούθηση ασθενών',
        items: ['Υπενθυμίσεις επανεξέτασης', 'Παρακολούθηση μετά από θεραπεία', 'Ενημέρωση για επόμενα ραντεβού']
      },
      clinicComm: {
        title: 'Διαχείριση επικοινωνίας κλινικής',
        items: ['Συντονισμός μεταξύ γιατρών και ασθενών', 'Οργάνωση καθημερινού προγράμματος', 'Διαχείριση αιτημάτων συνεργασιών']
      },
      newPatients: {
        title: 'Υποστήριξη νέων ασθενών',
        items: ['Ενημέρωση για υπηρεσίες ιατρείου', 'Καθοδήγηση για πρώτη επίσκεψη', 'Βασικές πληροφορίες για εξετάσεις']
      },
      medicalCenters: {
        title: 'Υποστήριξη μικρών ιατρικών κέντρων',
        items: ['Συντονισμός μεταξύ τμημάτων', 'Οργάνωση καθημερινής λειτουργίας', 'Διαχείριση ασθενών']
      },
      remoteSupport: {
        title: 'Εξ αποστάσεως γραμματειακή υποστήριξη',
        items: ['Remote secretary services', 'Ευέλικτη συνεργασία', 'Μείωση κόστους προσωπικού']
      },
      gesyReferrals: {
        title: 'Διαχείριση παραπεμπτικών ΓΕΣΥ',
        items: ['Έλεγχος εγκυρότητας παραπεμπτικών', 'Παρακολούθηση εγκρίσεων και ενημέρωση για τυχόν προβλήματα', 'Αναφορές για εσάς ή το προσωπικό σας', 'Εξοικονομήστε χρόνο και διασφαλίστε ότι όλα τα παραπεμπτικά είναι σωστά και έτοιμα']
      }
    },
    gdprSection: {
      title: 'Συμμόρφωση με νομοθεσία & προστασία προσωπικών δεδομένων',
      description: 'Οι υπηρεσίες μας είναι πλήρως εναρμονισμένες με τον GDPR και τη νομοθεσία ΓΕΣΥ, διασφαλίζοντας ότι όλα τα δεδομένα ασθενών διαχειρίζονται με ασφάλεια και εμπιστευτικότητα.',
    },
    whyUs: {
      title: 'Γιατί να Συνεργαστείτε με την J.House of Secretary;',
      subtitle: 'Ανακαλύψτε τα μοναδικά πλεονεκτήματα της συνεργασίας μαζί μας.',
      expertise: {
        title: 'Επαγγελματική Εξειδίκευση',
        description: 'Η ομάδα μας αποτελείται από έμπειρους επαγγελματίες με εκτενή πείρα στις τηλεφωνικές γραμματειακές υπηρεσίες.',
      },
      personalized: {
        title: 'Εξατομικευμένες Προσεγγίσεις',
        description: 'Καυχόμαστε για την ικανότητά μας να προσαρμόζουμε τις υπηρεσίες μας για να καλύψουμε τις συγκεκριμένες ανάγκες των πελατών μας.',
      },
      commitment: {
        title: 'Δέσμευση για Αριστεία',
        description: 'Η αδιάκοπη δέσμευσή μας για αριστεία και ικανοποίηση πελατών σημαίνει ότι ξεπερνούμε τον εαυτό μας για την καλύτερη δυνατή εμπειρία.',
      },
    },
    facilities: {
      title: 'Οι Σύγχρονες Εγκαταστάσεις μας',
      subtitle: 'Ανακαλύψτε τον υπερσύγχρονο χώρο γραφείων μας, σχεδιασμένο για επαγγελματικές τηλεγραμματειακές υπηρεσίες.',
    },
    testimonials: {
      title: 'Τι Λένε οι Πελάτες μας',
      subtitle: 'Ακούστε από επιχειρήσεις που μας εμπιστεύονται.',
      items: [
        {
          name: 'Δρ. Ανδρέας Κ.',
          role: 'Διευθυντής Ιατρικής Κλινικής',
          text: 'Η J.House of Secretary έχει μεταμορφώσει τον τρόπο που χειριζόμαστε τις κλήσεις ασθενών. Επαγγελματικοί, αποτελεσματικοί και πάντα διαθέσιμοι.',
        },
        {
          name: 'Μαρία Σ.',
          role: 'Ιδιοκτήτρια Επιχείρησης',
          text: 'Η εταιρική τηλε-γραμματειακή υπηρεσία είναι ανεκτίμητη. Οι πελάτες μας λαμβάνουν πάντα επαγγελματική προσοχή.',
        },
        {
          name: 'Χρήστος Π.',
          role: 'Συνέταιρος Δικηγορικού Γραφείου',
          text: 'Εξαιρετική υπηρεσία. Η πολυγλωσσική υποστήριξη είναι εξαιρετική και μας βοήθησε να επεκτείνουμε τη διεθνή πελατεία μας.',
        },
      ],
    },
    faq: {
      title: 'Συχνές Ερωτήσεις',
      subtitle: 'Βρείτε απαντήσεις σε κοινές ερωτήσεις σχετικά με τις υπηρεσίες μας.',
      items: [
        {
          question: 'Τι υπηρεσίες παρέχει το JHouseOfSecretary.com;',
          answer: 'Παρέχουμε πλήρη υποστήριξη για ιατρούς και κλινικές, συμπεριλαμβανομένων: διαχείριση τηλεφωνικών κλήσεων, προγραμματισμό ραντεβού, υπενθυμίσεις & έλεγχο παραπεμπτικών ΓΕΣΥ, αρχειοθέτηση και reporting.',
        },
        {
          question: 'Πώς καθορίζεται η τιμολόγηση;',
          answer: 'Η τιμολόγηση μας προσαρμόζεται στις ανάγκες του ιατρείου σας, με βάση τον όγκο κλήσεων, τις υπηρεσίες και τις ειδικές απαιτήσεις. Προσφέρουμε προσωποποιημένη και διαφανή προσφορά.',
        },
        {
          question: 'Υποστηρίζετε παραπεμπτικά ΓΕΣΥ;',
          answer: 'Ναι. Αναλαμβάνουμε έλεγχο και παρακολούθηση παραπεμπτικών ΓΕΣΥ, διασφαλίζοντας τη σωστή διαδικασία και αποφυγή λαθών.',
        },
        {
          question: 'Είναι ασφαλή τα δεδομένα των ασθενών;',
          answer: 'Ναι. Όλες οι διαδικασίες είναι πλήρως εναρμονισμένες με GDPR και νομοθεσία, διασφαλίζοντας απόλυτη εμπιστευτικότητα.',
        },
        {
          question: 'Πόσο κοστίζει σε σχέση με μόνιμο γραμματέα;',
          answer: 'Οι υπηρεσίες μας προσφέρουν πλήρη υποστήριξη με σημαντικά χαμηλότερο κόστος, χωρίς συμβόλαια ή επιπλέον δαπάνες.',
        },
        {
          question: 'Πόσο γρήγορα μπορώ να ξεκινήσω;',
          answer: 'Μπορούμε να ξεκινήσουμε την ίδια Μέρα με ομαλό onboarding και εκπαίδευση.',
        },
        {
          question: 'Υπάρχει δυνατότητα προσαρμογής των υπηρεσιών;',
          answer: 'Ναι. Προσφέρουμε πακέτα Basic, Professional και Premium, καθώς και εξατομικευμένες λύσεις ανάλογα με τις ανάγκες του ιατρείου.',
        },
        {
          question: 'Μπορώ να δοκιμάσω πριν δεσμευτώ;',
          answer: 'Ναι. Προσφέρουμε δωρεάν demo 10 λεπτών για να δείτε πώς λειτουργούμε.',
        },
        {
          question: 'Πώς διαχειρίζεστε τις κλήσεις όταν είμαι απασχολημένος;',
          answer: 'Αναλαμβάνουμε όλες τις κλήσεις, καταγράφουμε μηνύματα και ενημερώνουμε άμεσα το ιατρείο σας. Μπορούμε επίσης να προγραμματίζουμε ραντεβού και follow-up.',
        },
        {
          question: 'Συνεργάζεστε με μεγάλα νοσοκομεία ή κλινικές;',
          answer: 'Ναι, συνεργαζόμαστε με ιατρούς, διαγνωστικά κέντρα και ιατρικά ιδρύματα στην Κύπρο.',
        },
        {
          question: 'Είναι κατάλληλο για ιδιώτες γιατρούς και πολυϊατρεία;',
          answer: 'Ναι. Οι υπηρεσίες μας προσαρμόζονται σε κάθε τύπο ιατρείου, είτε πρόκειται για ιδιώτη είτε για πολυϊατρείο.',
        },
        {
          question: 'Τι γίνεται αν χρειάζομαι αλλαγές ή έξτρα υπηρεσίες;',
          answer: 'Μπορείτε οποιαδήποτε στιγμή να προσαρμόσετε ή να επεκτείνετε τις υπηρεσίες σας, ανάλογα με τις ανάγκες σας.',
        },
        {
          question: 'Πώς επικοινωνώ για προσφορά ή demo;',
          answer: 'Μέσω φόρμας επικοινωνίας, email, WhatsApp ή τηλεφωνικά.',
        },
        {
          question: 'Υποστηρίζετε υπενθυμίσεις για ραντεβού;',
          answer: 'Ναι. Στέλνουμε υπενθυμίσεις στους ασθενείς, μειώνοντας ακυρώσεις και καθυστερήσεις.',
        },
        {
          question: 'Μπορείτε να διαχειριστείτε πολλαπλούς γιατρούς ή τμήματα;',
          answer: 'Ναι. Προσαρμόζουμε τη ροή κλήσεων και ραντεβού για κάθε γιατρό ή τμήμα του ιατρείου.',
        },
        {
          question: 'Υπάρχει όριο στον αριθμό των κλήσεων που μπορώ να στείλω;',
          answer: 'Η τιμολόγηση βασίζεται στον όγκο, αλλά μπορούμε να καλύψουμε από λίγες μέχρι εκατοντάδες κλήσεις ημερησίως.',
        },
        {
          question: 'Πώς γίνεται η καταγραφή των παραπεμπτικών;',
          answer: 'Χρησιμοποιούμε ασφαλή ηλεκτρονικά συστήματα για καταγραφή, αρχειοθέτηση και reporting, ώστε να έχετε πλήρη εικόνα.',
        },
        {
          question: 'Μπορώ να λάβω αναφορές ή στατιστικά;',
          answer: 'Ναι. Παρέχουμε αναλυτικές αναφορές για κλήσεις, ραντεβού, παραπεμπτικά και follow-up.',
        },
        {
          question: 'Υπάρχει τηλεφωνική ή online υποστήριξη για εμάς;',
          answer: 'Ναι. Έχουμε dedicated support για εσάς ανά πάσα στιγμή.',
        },
        {
          question: 'Είναι οι γραμματείς σας εκπαιδευμένοι σε ιατρικά θέματα;',
          answer: 'Ναι. Έχουν εμπειρία στην υποστήριξη ιατρικών ιατρείων και είναι εξοικειωμένοι με διαδικασίες ΓΕΣΥ.',
        },
        {
          question: 'Πώς διασφαλίζεται η ποιότητα των υπηρεσιών;',
          answer: 'Κάθε κλήση, καταχώρηση και follow-up ελέγχεται για ποιότητα, ενώ υπάρχει συνεχής βελτίωση διαδικασιών.',
        },
        {
          question: 'Πώς αντιμετωπίζετε έκτακτες καταστάσεις ή επείγοντα;',
          answer: 'Σε επείγουσες περιπτώσεις, ενημερώνουμε άμεσα τον γιατρό ή το προσωπικό του ιατρείου σύμφωνα με τις οδηγίες σας.',
        },
        {
          question: 'Μπορείτε να διαχειριστείτε ηλεκτρονικά ραντεβού μέσω πλατφορμών;',
          answer: 'Ναι. Υποστηρίζουμε προγραμματισμό και διαχείριση ραντεβού μέσω ηλεκτρονικών πλατφορμών, email ή τηλεφώνου.',
        },
        {
          question: 'Υπάρχει δυνατότητα προσωποποίησης μηνυμάτων ασθενών;',
          answer: 'Ναι. Μπορούμε να προσαρμόσουμε υπενθυμίσεις και follow-up σύμφωνα με τις ανάγκες και την επικοινωνιακή ταυτότητα του ιατρείου.',
        },
        {
          question: 'Πώς εξοικονομώ χρόνο με τις υπηρεσίες σας;',
          answer: 'Αναλαμβάνουμε όλες τις γραμματειακές διαδικασίες, επιτρέποντάς σας να εστιάσετε αποκλειστικά στους ασθενείς σας, ενώ μειώνουμε τα λάθη και τις καθυστερήσεις.',
        },
      ],
    },
    contact: {
      title: 'Επικοινωνήστε Μαζί Μας',
      subtitle: 'Ελάτε σε επαφή με την ομάδα μας για εξατομικευμένη συμβουλευτική.',
      form: {
        name: 'Το Όνομά Σας',
        email: 'Διεύθυνση Email',
        phone: 'Αριθμός Τηλεφώνου',
        message: 'Το Μήνυμά Σας',
        submit: 'Αποστολή Μηνύματος',
        sending: 'Αποστολή...',
        success: 'Το μήνυμα στάλθηκε επιτυχώς!',
        error: 'Αποτυχία αποστολής. Παρακαλώ δοκιμάστε ξανά.',
      },
      info: {
        phone: 'Τηλέφωνο',
        mobile: 'Κινητό',
        email: 'Email',
        address: 'Διεύθυνση',
        addressLine: 'Αφροδίτης 10, Λευκωσία 1060, Κύπρος',
      },
    },
    chat: {
      title: 'Ζωντανή Συνομιλία',
      placeholder: 'Πληκτρολογήστε το μήνυμά σας...',
      welcome: 'Γεια σας! Πώς μπορούμε να σας βοηθήσουμε σήμερα;',
      offline: 'Αυτή τη στιγμή είμαστε εκτός σύνδεσης. Αφήστε μας ένα μήνυμα.',
    },
    footer: {
      rights: 'Με Επιφύλαξη Παντός Δικαιώματος',
      privacy: 'Πολιτική Απορρήτου',
      terms: 'Όροι Χρήσης',
    },
    partners: {
      title: 'Οι Συνεργάτες μας',
      subtitle: 'Μας εμπιστεύονται κορυφαία ιδρύματα υγείας στην Κύπρο',
    },
    patientLine: {
      back: 'Πίσω',
      phone: '7000 0223',
      tagline: 'Η Νέα Πρωτοποριακή Υπηρεσία από την J.House of Secretary',
      title: 'Η Γραμμή Του Ασθενή',
      slogan: '"Η υγεία σας, ένα βήμα μπροστά."',
      free: 'Δωρεάν για όλους τους ασθενείς — χωρίς καμία χρέωση.',
      mainMessage: 'Τέλος στην ταλαιπωρία του ασθενή.',
      subMessage: 'Εσείς καλείτε — εμείς το αναλαμβάνουμε.',
      servicesTitle: 'Τι αναλαμβάνουμε για εσάς',
      services: [
        'Κλείσιμο ραντεβού με την ειδικότητα που θέλετε',
        'Πρόσβαση σε ακτινολογικά & αιματολογικά κέντρα',
        'Εύρεση κοντινότερου φαρμακείου',
        'Υπενθυμίσεις ραντεβού',
        'Follow-up μετά την εξέταση'
      ],
      stepsTitle: 'Βήματα για τους Ασθενείς',
      stepsSubtitle: 'Απλά 4 βήματα για να λύσετε κάθε ανάγκη υγείας',
      steps: [
        { number: '01', title: 'Καλέστε τη Γραμμή Ασθενή', description: 'Καλέστε το 7000 0223 / 801 101 0364 και μιλήστε με τους εξειδικευμένους συνεργάτες μας.' },
        { number: '02', title: 'Πείτε μας τι χρειάζεστε', description: 'Ραντεβού με ειδικότητα, εξέταση, πρόσβαση σε διαγνωστικό κέντρο ή φαρμακείο.' },
        { number: '03', title: 'Εμείς αναλαμβάνουμε τα πάντα', description: 'Κλείνουμε ραντεβού, ελέγχουμε παραπεμπτικά, βρίσκουμε το κοντινότερο σημείο εξυπηρέτησης.' },
        { number: '04', title: 'Λαμβάνετε επιβεβαίωση', description: 'Σας ενημερώνουμε για την ημερομηνία, ώρα και τοποθεσία του ραντεβού σας.' }
      ],
      benefitsTitle: 'Τέλος στο Άγχος',
      benefitsSubtitle: 'Γιατί να επιλέξετε τη Γραμμή Ασθενή',
      benefits: [
        { title: 'Ένα τηλέφωνο για όλα', description: 'Καλέστε μόνο το 7000 0223 / 801 101 0364 για κάθε ανάγκη υγείας.' },
        { title: 'Εξοικονόμηση χρόνου', description: 'Τέλος στην αναμονή και τις ατέλειωτες κλήσεις.' },
        { title: '100% Δωρεάν', description: 'Χωρίς καμία χρέωση για τους ασθενείς.' },
        { title: 'Προσωπική φροντίδα', description: 'Εξατομικευμένη εξυπηρέτηση για κάθε ασθενή.' },
        { title: 'GDPR Compliant', description: 'Απόλυτη προστασία των προσωπικών σας δεδομένων.' }
      ],
      ctaTitle: 'Καλέστε τώρα',
      ctaSubtitle: 'Διαθέσιμοι καθημερινά για να σας εξυπηρετήσουμε',
      compatible: 'Συμβατό με',
    },
    international: {
      back: 'Πίσω',
      pageTitle: 'Διεθνής Πρόσβαση Υγείας',
      subtitle: 'Συντονισμός για ασθενείς που αναζητούν εξειδικευμένες λύσεις στο εξωτερικό',
      description: 'Αναλαμβάνουμε τον επαγγελματικό συντονισμό ασθενών που αναζητούν προηγμένες ιατρικές υπηρεσίες, δεύτερη γνώμη ή θεραπεία σε αναγνωρισμένα διεθνή κέντρα υγείας. Με απόλυτη διακριτικότητα, σωστή καθοδήγηση και εξατομικευμένη υποστήριξη σε κάθε στάδιο.',
      missionQuote: '"Helping healthcare work better — Εξειδικευμένες λύσεις φροντίδας πέρα από τα σύνορα"',
      servicesTitle: 'Υπηρεσίες Διεθνούς Συντονισμού',
      service1Title: 'Συντονισμός ιατρικών περιστατικών στο εξωτερικό',
      service1Desc: 'Αναλαμβάνουμε πλήρως τον συντονισμό για θεραπεία σε αναγνωρισμένα διεθνή κέντρα',
      service2Title: 'Δεύτερη ιατρική γνώμη',
      service2Desc: 'Πρόσβαση σε εξειδικευμένους ιατρούς από κορυφαία ιδρύματα',
      service3Title: 'Υποστήριξη ιατρικού φακέλου & επικοινωνίας',
      service3Desc: 'Πλήρης διαχείριση εγγράφων, μεταφράσεων και αλληλογραφίας',
      service4Title: 'Οργάνωση ραντεβού και διαδικασιών',
      service4Desc: 'Κλείσιμο ραντεβού, προετοιμασία εξετάσεων, logistics',
      service5Title: 'Προσωποποιημένη καθοδήγηση ασθενών',
      service5Desc: 'Εξατομικευμένη υποστήριξη σε κάθε στάδιο της διαδικασίας',
      service6Title: 'Premium Patient Assistance',
      service6Desc: 'Υπηρεσίες concierge για VIP ασθενείς με απόλυτη διακριτικότητα',
      whoTitle: 'Για Ποιους Απευθύνεται',
      patients: 'Ασθενείς',
      patientsDesc: 'Σας βοηθούμε να οργανώσετε ασφαλή πρόσβαση σε εξειδικευμένα κέντρα υγείας, με πλήρη υποστήριξη από την Κύπρο μέχρι την άφιξή σας',
      doctorsTitle: 'Γιατροί & Κλινικές',
      doctorsDesc: 'Αναλαμβάνουμε διοικητικό και διεθνή συντονισμό σύνθετων περιστατικών ώστε ο ασθενής να εξυπηρετείται σωστά',
      hospitalsTitle: 'Νοσοκομεία & Ιδρύματα',
      hospitalsDesc: 'Μέσω διεθνών συνεργασιών με αναγνωρισμένους οργανισμούς υγείας, παρέχουμε πρόσβαση σε εξειδικευμένες υπηρεσίες',
      ctaTitle: 'Χρειάζεστε Εξειδικευμένη Θεραπεία στο Εξωτερικό;',
      ctaDesc: 'Γρήγορη πρόσβαση σε ειδικούς μέσω του γραφείου συντονισμού μας στην Κύπρο',
      ctaConsultation: 'Αίτημα Συνεδρίας',
      ctaUpload: 'Αποστολή Ιατρικών Εγγράφων',
      ctaCall: 'Επικοινωνία με Συντονιστή',
      faqTitle: 'Συχνές Ερωτήσεις',
      faqSubtitle: 'Διεθνής Πρόσβαση Υγείας',
      featuredPartnerLabel: 'Συνεργαζόμενο Ιατρικό Κέντρο',
      holisticTagline: 'Ιατρική Φροντίδα Παγκόσμιου Επιπέδου στο Ισραήλ',
      holisticDescription: 'Το Sheba Medical Center στο Tel HaShomer κατατάσσεται σταθερά ανάμεσα στα κορυφαία νοσοκομεία του κόσμου (Newsweek) και ξεχωρίζει διεθνώς στην ογκολογία, καρδιολογία, μεταμοσχεύσεις, γονιμότητα, νευρολογία και σύγχρονη χειρουργική. Μέσω του Sheba Global και σε συντονισμό με τη J.House of Secretary, οι διεθνείς ασθενείς έχουν πρόσβαση σε κορυφαίους ιατρούς, πρωτοποριακή έρευνα και πλήρως εξατομικευμένη, πολύγλωσση διαχείριση περιστατικού — από την πρώτη επαφή έως τη μετεγχειρητική παρακολούθηση.',
      holisticTags: ['Ογκολογία', 'Καρδιολογία', 'Μεταμοσχεύσεις', 'Διεθνείς Ασθενείς'],
      uploadModal: {
        title: 'Αποστολή Ιατρικών Εγγράφων',
        subtitle: 'Στείλτε με ασφάλεια τις ιατρικές σας εξετάσεις και αρχεία',
        nameLabel: 'Ονοματεπώνυμο',
        phoneLabel: 'Τηλέφωνο',
        emailLabel: 'Email',
        messageLabel: 'Μήνυμα (προαιρετικό)',
        messagePlaceholder: 'Περιγράψτε σύντομα την περίπτωσή σας...',
        filesLabel: 'Αρχεία / Φάκελοι',
        filesHint: 'Δεκτοί όλοι οι τύποι αρχείων • Μέγιστο μέγεθος 25MB συνολικά',
        selectFiles: 'Επιλογή Αρχείων',
        selectFolder: 'Επιλογή Φακέλου',
        clearFiles: 'Καθαρισμός',
        gdprConsent: 'Συναινώ στην επεξεργασία των δεδομένων μου σύμφωνα με τον GDPR για τους σκοπούς αυτού του αιτήματος.',
        submitBtn: 'Αποστολή',
        cancelBtn: 'Ακύρωση',
        sending: 'Αποστολή...',
        successMsg: 'Το αίτημά σας στάλθηκε επιτυχώς. Θα επικοινωνήσουμε μαζί σας σύντομα.',
        errorMsg: 'Η αποστολή απέτυχε. Δοκιμάστε ξανά ή επικοινωνήστε μαζί μας απευθείας.',
        sizeError: 'Το συνολικό μέγεθος αρχείων υπερβαίνει το όριο των 25MB.',
        requiredError: 'Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία.',
        gdprError: 'Παρακαλώ αποδεχτείτε τη συναίνεση GDPR για να συνεχίσετε.',
        rateLimitError: 'Πολλά αιτήματα. Παρακαλώ δοκιμάστε ξανά σε {minutes} λεπτά.',
        filesSelected: 'αρχεία επιλεγμένα',
      },
      faqItems: [
        { q: 'Τι είναι η υπηρεσία Διεθνής Πρόσβαση Υγείας;', a: 'Πρόκειται για υπηρεσία επαγγελματικού συντονισμού για ασθενείς που αναζητούν δεύτερη γνώμη, εξειδικευμένη θεραπεία ή πρόσβαση σε αναγνωρισμένα διεθνή κέντρα υγείας.' },
        { q: 'Παρέχετε ιατρικές συμβουλές;', a: 'Όχι. Δεν παρέχουμε ιατρική διάγνωση ή θεραπεία. Αναλαμβάνουμε τον διοικητικό και οργανωτικό συντονισμό μεταξύ ασθενών και διεθνών παρόχων υγείας.' },
        { q: 'Πώς ξεκινά η διαδικασία;', a: 'Απλώς επικοινωνείτε μαζί μας και μας αποστέλλετε βασικές πληροφορίες ή διαθέσιμες εξετάσεις, ώστε να αξιολογηθεί η καταλληλότερη πορεία συντονισμού.' },
        { q: 'Χρειάζεται να ταξιδέψω στο εξωτερικό;', a: 'Όχι πάντα. Σε αρκετές περιπτώσεις μπορεί να οργανωθεί αρχικά δεύτερη γνώμη ή αξιολόγηση εξ αποστάσεως.' },
        { q: 'Μπορώ να στείλω εξετάσεις ηλεκτρονικά;', a: 'Ναι. Μπορείτε να αποστείλετε εξετάσεις, γνωματεύσεις και ιατρικά έγγραφα ηλεκτρονικά με ασφαλή τρόπο.' },
        { q: 'Σε ποιες χώρες μπορείτε να βοηθήσετε;', a: 'Ανάλογα με το περιστατικό και τη διαθεσιμότητα συνεργαζόμενων οργανισμών, παρέχουμε πρόσβαση σε επιλεγμένα διεθνή κέντρα υγείας.' },
        { q: 'Πόσο γρήγορα μπορεί να προχωρήσει ένα αίτημα;', a: 'Ο χρόνος διαφέρει ανάλογα με την περίπτωση, όμως δίνουμε προτεραιότητα σε επείγοντα και σοβαρά περιστατικά με άμεσο συντονισμό.' },
        { q: 'Παρέχετε υπηρεσίες στα ελληνικά;', a: 'Ναι. Παρέχουμε πλήρη υποστήριξη στα ελληνικά και, όπου χρειάζεται, στα αγγλικά.' },
        { q: 'Ποια είδη περιστατικών εξυπηρετείτε;', a: 'Υποστηρίζουμε περιπτώσεις όπως δεύτερη γνώμη, σύνθετα περιστατικά, εξειδικευμένες θεραπείες, ογκολογία, καρδιολογία, παιδιατρικά, νευρολογία και άλλα.' },
        { q: 'Αναλαμβάνετε και τα ραντεβού;', a: 'Ναι. Μπορούμε να βοηθήσουμε στον προγραμματισμό ραντεβού, διαδικασιών και σχετικών βημάτων.' },
        { q: 'Παρέχετε υποστήριξη κατά τη διάρκεια της θεραπείας;', a: 'Ναι. Παραμένουμε διαθέσιμοι για διοικητικό συντονισμό και καθοδήγηση σε κάθε στάδιο.' },
        { q: 'Τι γίνεται με την εμπιστευτικότητα των προσωπικών μου στοιχείων;', a: 'Όλες οι πληροφορίες διαχειρίζονται με επαγγελματισμό, διακριτικότητα και σεβασμό στην ιδιωτικότητα.' },
        { q: 'Πόσο κοστίζει η υπηρεσία;', a: 'Το κόστος εξαρτάται από το είδος του αιτήματος και τις απαιτήσεις συντονισμού. Παρέχεται σχετική ενημέρωση πριν την έναρξη.' },
        { q: 'Μπορώ να ζητήσω μόνο δεύτερη γνώμη;', a: 'Βεβαίως. Πολλοί ασθενείς ζητούν αρχικά δεύτερη γνώμη πριν αποφασίσουν επόμενα βήματα.' },
        { q: 'Συνεργάζεστε με γιατρούς και κλινικές;', a: 'Ναι. Υποστηρίζουμε επίσης επαγγελματίες υγείας, κλινικές και οργανισμούς που χρειάζονται διεθνή συντονισμό περιστατικών.' },
        { q: 'Εξυπηρετείτε όλη την Κύπρο;', a: 'Ναι. Παρέχουμε παγκύπρια εξυπηρέτηση με δυνατότητα απομακρυσμένης επικοινωνίας.' },
        { q: 'Μπορώ να επικοινωνήσω μέσω WhatsApp;', a: 'Ναι. Διατίθεται άμεση επικοινωνία μέσω τηλεφώνου και WhatsApp.' },
        { q: 'Ποιος είναι ο ρόλος σας στη διαδικασία;', a: 'Ο ρόλος μας είναι να απλοποιούμε μια σύνθετη διαδικασία και να λειτουργούμε ως σημείο συντονισμού μεταξύ ασθενούς και διεθνών φορέων υγείας.' },
        { q: 'Τι γίνεται σε επείγουσες περιπτώσεις;', a: 'Μπορείτε να επικοινωνήσετε άμεσα μαζί μας ώστε να αξιολογηθεί ο ταχύτερος δυνατός τρόπος υποστήριξης.' },
        { q: 'Πώς μπορώ να ξεκινήσω σήμερα;', a: 'Μπορείτε να επικοινωνήσετε μαζί μας τηλεφωνικά, μέσω WhatsApp ή από τη φόρμα επικοινωνίας της ιστοσελίδας.' },
      ],
      footer: 'Συντονισμός Υγειονομικής Περίθαλψης • Διεθνής Πρόσβαση • Επαγγελματική Υποστήριξη',
    },
    cookieConsent: {
      title: 'Χρησιμοποιούμε cookies',
      message: 'Αυτή η ιστοσελίδα χρησιμοποιεί cookies για βέλτιστη εμπειρία πλοήγησης. Χρησιμοποιούμε μόνο τα απαραίτητα cookies για βασική λειτουργικότητα και αναλυτικά εργαλεία για τη βελτίωση των υπηρεσιών μας.',
      learnMore: 'Μάθετε περισσότερα',
      acceptAll: 'Αποδοχή Όλων',
      necessaryOnly: 'Μόνο Απαραίτητα',
      policyTitle: 'Πολιτική Cookies',
      section1Title: 'Τι είναι τα cookies;',
      section1Body: 'Τα cookies είναι μικρά αρχεία κειμένου που αποθηκεύονται στη συσκευή σας όταν επισκέπτεστε μια ιστοσελίδα. Βοηθούν στη σωστή λειτουργία της σελίδας και σε καλύτερη εμπειρία πλοήγησης.',
      section2Title: 'Πώς χρησιμοποιούμε τα cookies',
      section2Body: 'Χρησιμοποιούμε cookies για βασική λειτουργικότητα (sessions σύνδεσης, επιλογή γλώσσας) και για την κατανόηση του πώς οι επισκέπτες αλληλεπιδρούν με την ιστοσελίδα μας.',
      section3Title: 'Είδη cookies που χρησιμοποιούμε',
      cookieType1: 'Απαραίτητα cookies — απαιτούνται για τη λειτουργία της σελίδας (σύνδεση, γλώσσα, session)',
      cookieType2: 'Λειτουργικά cookies — θυμούνται τις προτιμήσεις σας (θέμα, ρυθμίσεις)',
      cookieType3: 'Αναλυτικά cookies — μας βοηθούν να κατανοήσουμε τη χρήση (ανώνυμα)',
      section4Title: 'Τα δικαιώματά σας (GDPR)',
      section4Body: 'Μπορείτε να αλλάξετε ή να ανακαλέσετε τη συγκατάθεσή σας οποτεδήποτε διαγράφοντας τα δεδομένα του browser σας. Έχετε επίσης δικαίωμα πρόσβασης, διόρθωσης ή διαγραφής των προσωπικών σας δεδομένων σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων της ΕΕ.',
      contactTitle: 'Επικοινωνία',
    },
  },
  ru: {
    sheba: { badge: 'АВТОРИЗОВАННЫЕ ПРЕДСТАВИТЕЛИ SHEBA' },
    splash: {
      back: 'Назад',
      tagline: 'Координация здравоохранения • Международный доступ • Профессиональная поддержка',
      forDoctors: 'Для врачей клиник больниц',
      forPatients: 'Для пациентов',
      internationalAccess: 'Международный доступ к здравоохранению',
      trustedBy: 'НАМ ДОВЕРЯЮТ',
      nightPharmacies: 'НОЧНЫЕ АПТЕКИ',
      nightPharmaciesDesc: 'Найдите ближайшую ночную аптеку в вашем районе',
      nightPharmaciesFooter: 'Данные обновляются ежедневно.',
      disclaimer1: 'Линия пациента предоставляет услуги по поиску доступных врачей и записи на приём.',
      disclaimer2: 'Служба может предложить доступных партнёрских врачей на основе доступности и потребностей пациента, не влияя на медицинские решения.',
      disclaimer3: 'Окончательный выбор врача делается исключительно пациентом.',
      disclaimer4: 'Партнёрские врачи участвуют через годовую подписку для участия в сети и управления запросами.',
      disclaimer5: 'Служба не предоставляет медицинских консультаций и не заменяет отношения врач-пациент.',
    },
    philosophy: {
      eyebrow: 'НАША ФИЛОСОФИЯ',
      etymology: 'Греческое слово «ασθενής» (пациент) происходит от приставки «α-» и «σθένος» (сила) — обозначая того, кто нуждается в поддержке, заботе и возвращении силы.',
      paragraphs: [
        'Мы верим, что каждое обращение к специалисту в области здравоохранения — это возможность вернуть человеку немного этой силы. Именно поэтому мы уделяем особое внимание первому звонку, где вежливость, понимание и профессионализм могут изменить всё.',
        'В J.House of Secretary мы не просто принимаем звонки и записываем на приём. Мы поддерживаем людей в моменты, когда им нужна помощь, забота и доступ к нужным медицинским услугам.',
        'Через службы секретарского сопровождения, Линию Пациента и Международный Доступ к Здравоохранению мы обеспечиваем, чтобы каждый человек чувствовал, что его слышат, понимают и сопровождают с уважением и ответственностью.',
        'От первого телефонного звонка до подключения к квалифицированным специалистам на Кипре и за рубежом — наша миссия — превращать неопределённость в ясное руководство, препятствия — в решения, а тревогу — в чувство уверенности.',
      ],
      closing: 'Потому что за каждым звонком стоит не просто пациент, а человек, который заслуживает чувствовать, что он не одинок на пути к здоровью.',
    },
    nav: {
      home: 'Главная',
      services: 'Услуги',
      whyUs: 'Почему Мы',
      testimonials: 'Отзывы',
      faq: 'Вопросы',
      contact: 'Контакты',
      pricing: 'Цены',
    },
    hero: {
      tagline: 'Ваш Надежный Партнер в Профессиональных Секретарских Услугах',
      headline: 'Никогда Больше Не Пропустите Звонок Пациента',
      title: 'Добро Пожаловать в',
      subtitle: 'J.House of Secretary',
      description: 'Ваш надежный партнер в профессиональных, корпоративных и медицинских телефонных секретарских услугах, посвященный повышению эффективности вашего бизнеса.',
      cta: 'Свяжитесь с Нами',
      ctaSecondary: 'Изучить Услуги',
      ctaConsultation: 'Записаться на Бесплатную Консультацию',
      available: '24/7 Доступно',
    },
    heroFeatures: {
      callAnswering: 'Ответ на звонки 24/7',
      appointmentReminders: 'Напоминания о приеме',
      patientFollowUp: 'Наблюдение за пациентами',
      telemedicineScheduling: 'Планирование телемедицины',
      patientIntake: 'Прием пациентов',
      clinicWorkflow: 'Поддержка работы клиники',
    },
    services: {
      title: 'Комплексные Телефонные Секретарские Услуги',
      subtitle: 'Оптимизируйте свои операции с поддержкой наших специализированных телефонных секретарей.',
      corporate: {
        title: 'Корпоративная Поддержка',
        description: 'Наши корпоративные услуги включают управление звонками, планирование встреч и административную поддержку.',
      },
      medical: {
        title: 'Медицинские Услуги',
        description: 'Разработанные для медицинских работников, наши услуги обеспечивают профессиональную обработку звонков пациентов.',
      },
      virtual: {
        title: 'Виртуальный Помощник',
        description: 'Наши услуги виртуального помощника обеспечивают гибкую и надежную поддержку.',
      },
      pricing: {
        title: 'Цены',
        description: 'Цены в зависимости от объема ваших звонков.',
      },
      gesy: {
        title: 'GESY',
        description: 'Мы сотрудничаем с: Частными клиниками, Диагностическими центрами, Медицинскими специалистами, Крупными медицинскими организациями Кипра. Наши услуги поддерживают медицинские команды в управлении коммуникацией с пациентами.',
      },
    },
    detailedServices: {
      title: 'Наши Услуги',
      intro: 'В J House of Secretary мы предоставляем специализированные услуги медицинской секретарской поддержки для частных практик, клиник и медицинских центров.',
      phoneCalls: { title: 'Управление звонками пациентов', items: ['Ответ на входящие звонки', 'Запись сообщений пациентов', 'Переадресация важных звонков врачу', 'Управление запросами пациентов', 'Информирование о часах работы'] },
      appointments: { title: 'Управление записью', items: ['Запись на прием', 'Изменение и отмена записи', 'Организация календаря врача', 'Предотвращение двойных записей', 'Напоминания о приеме'] },
      emailManagement: { title: 'Управление email', items: ['Мониторинг входящих писем', 'Ответы на базовые запросы', 'Организация корреспонденции', 'Переадресация важных сообщений'] },
      patientComm: { title: 'Коммуникация с пациентами', items: ['Информирование о записи', 'Follow-up после визита', 'Ответы на вопросы', 'Управление запросами'] },
      patientIntake: { title: 'Прием пациентов', items: ['Регистрация новых пациентов', 'Сбор информации до приема', 'Организация форм'] },
      adminSupport: { title: 'Административная поддержка', items: ['Организация файлов', 'Управление документами', 'Подготовка отчетов', 'Ежедневная поддержка'] },
      telemedicine: { title: 'Поддержка телемедицины', items: ['Организация онлайн-консультаций', 'Отправка ссылок', 'Техническая помощь'] },
      followUp: { title: 'Follow-up пациентов', items: ['Напоминания о повторном осмотре', 'Follow-up после лечения', 'Информация о следующих приемах'] },
      clinicComm: { title: 'Управление коммуникацией', items: ['Координация врачей и пациентов', 'Организация расписания', 'Управление запросами'] },
      newPatients: { title: 'Поддержка новых пациентов', items: ['Информация об услугах', 'Руководство для первого визита', 'Базовая информация'] },
      medicalCenters: { title: 'Поддержка медцентров', items: ['Координация отделений', 'Организация работы', 'Управление пациентами'] },
      remoteSupport: { title: 'Удаленная поддержка', items: ['Удаленные секретарские услуги', 'Гибкое сотрудничество', 'Снижение затрат'] },
      gesyReferrals: { title: 'Управление направлениями GESY', items: ['Проверка действительности направлений', 'Отслеживание одобрений и уведомление о проблемах', 'Отчеты для вас или персонала', 'Экономьте время и гарантируйте правильность направлений'] }
    },
    gdprSection: {
      title: 'Соответствие законодательству и защита данных',
      description: 'Наши услуги полностью соответствуют GDPR и законодательству GESY, обеспечивая безопасную и конфиденциальную обработку данных пациентов.',
    },
    whyUs: {
      title: 'Почему J.House of Secretary?',
      subtitle: 'Откройте для себя уникальные преимущества работы с нами.',
      expertise: {
        title: 'Профессионализм',
        description: 'Наша команда состоит из опытных профессионалов.',
      },
      personalized: {
        title: 'Индивидуальный Подход',
        description: 'Мы гордимся способностью адаптировать услуги под ваши потребности.',
      },
      commitment: {
        title: 'Преданность Качеству',
        description: 'Наше неизменное стремление к совершенству.',
      },
    },
    facilities: {
      title: 'Наши Современные Объекты',
      subtitle: 'Откройте для себя наш современный офис, созданный для профессиональных услуг телесекретаря.',
    },
    testimonials: {
      title: 'Отзывы Клиентов',
      subtitle: 'Узнайте, что говорят о нас наши клиенты.',
      items: [
        { name: 'Д-р Андреас К.', role: 'Директор Клиники', text: 'J.House of Secretary изменила способ обработки звонков пациентов.' },
        { name: 'Мария С.', role: 'Владелец Бизнеса', text: 'Корпоративные услуги были неоценимы.' },
        { name: 'Кристос П.', role: 'Юридический Партнер', text: 'Отличный сервис. Многоязычная поддержка исключительна.' },
      ],
    },
    faq: {
      title: 'Часто Задаваемые Вопросы',
      subtitle: 'Найдите ответы на распространенные вопросы.',
      items: [
        { question: 'Какие услуги вы предлагаете?', answer: 'Мы предлагаем комплексные телефонные секретарские услуги.' },
        { question: 'Доступны ли ваши услуги 24/7?', answer: 'Да, наша команда доступна круглосуточно.' },
        { question: 'Какие языки вы поддерживаете?', answer: 'Греческий, английский, русский, арабский, китайский, французский и испанский.' },
        { question: 'Как определяется ценообразование?', answer: 'Цены основаны на объеме звонков.' },
        { question: 'Как начать?', answer: 'Просто свяжитесь с нами по телефону или email.' },
      ],
    },
    contact: {
      title: 'Свяжитесь с Нами',
      subtitle: 'Получите персональную консультацию.',
      form: {
        name: 'Ваше Имя',
        email: 'Email',
        phone: 'Телефон',
        message: 'Сообщение',
        submit: 'Отправить',
        sending: 'Отправка...',
        success: 'Сообщение отправлено!',
        error: 'Ошибка. Попробуйте снова.',
      },
      info: {
        phone: 'Телефон',
        mobile: 'Мобильный',
        email: 'Email',
        address: 'Адрес',
        addressLine: 'Афродитис 10, Никосия 1060, Кипр',
      },
    },
    chat: {
      title: 'Чат',
      placeholder: 'Введите сообщение...',
      welcome: 'Здравствуйте! Чем можем помочь?',
      offline: 'Мы сейчас офлайн. Оставьте сообщение.',
    },
    footer: {
      rights: 'Все Права Защищены',
      privacy: 'Конфиденциальность',
      terms: 'Условия',
    },
    partners: {
      title: 'Наши Партнеры',
      subtitle: 'Нам доверяют ведущие медицинские учреждения Кипра',
    },
    patientLine: {
      back: 'Назад',
      phone: '7000 0223',
      tagline: 'Новая Инновационная Услуга от J.House of Secretary',
      title: 'Линия Пациента',
      slogan: '"Ваше здоровье на шаг впереди."',
      free: 'Бесплатно для всех пациентов — без оплаты.',
      mainMessage: 'Конец мучениям пациента.',
      subMessage: 'Вы звоните — мы всё решаем.',
      servicesTitle: 'Что мы делаем для вас',
      services: [
        'Запись на приём к нужному специалисту',
        'Доступ к радиологическим и гематологическим центрам',
        'Поиск ближайшей аптеки',
        'Напоминания о приёмах',
        'Сопровождение после обследования'
      ],
      stepsTitle: 'Шаги для Пациентов',
      stepsSubtitle: 'Всего 4 шага для решения любой медицинской потребности',
      steps: [
        { number: '01', title: 'Позвоните на Линию Пациента', description: 'Позвоните 7000 0223 / 801 101 0364 и поговорите с нашими специалистами.' },
        { number: '02', title: 'Расскажите, что вам нужно', description: 'Приём у специалиста, обследование, доступ в диагностический центр или аптеку.' },
        { number: '03', title: 'Мы всё организуем', description: 'Записываем на приём, проверяем направления, находим ближайший пункт обслуживания.' },
        { number: '04', title: 'Получите подтверждение', description: 'Мы сообщим вам дату, время и место вашего приёма.' }
      ],
      benefitsTitle: 'Конец Стрессу',
      benefitsSubtitle: 'Почему выбирают Линию Пациента',
      benefits: [
        { title: 'Один телефон для всего', description: 'Звоните только на 7000 0223 / 801 101 0364 для любых медицинских потребностей.' },
        { title: 'Экономия времени', description: 'Больше никакого ожидания и бесконечных звонков.' },
        { title: '100% Бесплатно', description: 'Никакой оплаты для пациентов.' },
        { title: 'Персональная забота', description: 'Индивидуальное обслуживание для каждого пациента.' },
        { title: 'Соответствует GDPR', description: 'Абсолютная защита ваших личных данных.' }
      ],
      ctaTitle: 'Позвоните сейчас',
      ctaSubtitle: 'Мы доступны ежедневно',
      compatible: 'Совместимо с',
    },
    international: {
      back: 'Назад',
      pageTitle: 'Международный доступ к здравоохранению',
      subtitle: 'Координация для пациентов, ищущих специализированные решения за рубежом',
      description: 'Мы берём на себя профессиональную координацию пациентов, ищущих передовые медицинские услуги, второе мнение или лечение в признанных международных центрах здоровья. С абсолютной конфиденциальностью и персонализированной поддержкой на каждом этапе.',
      missionQuote: '"Helping healthcare work better — Специализированные решения по уходу за пределами границ"',
      servicesTitle: 'Услуги международной координации',
      service1Title: 'Координация медицинских случаев за рубежом',
      service1Desc: 'Полная координация лечения в признанных международных центрах',
      service2Title: 'Второе медицинское мнение',
      service2Desc: 'Доступ к специализированным врачам ведущих учреждений',
      service3Title: 'Поддержка медицинских документов и коммуникации',
      service3Desc: 'Полное управление документами, переводами и перепиской',
      service4Title: 'Организация приёмов и процедур',
      service4Desc: 'Запись на приём, подготовка обследований, логистика',
      service5Title: 'Персонализированное сопровождение пациентов',
      service5Desc: 'Индивидуальная поддержка на каждом этапе процесса',
      service6Title: 'Premium Patient Assistance',
      service6Desc: 'Консьерж-услуги для VIP-пациентов с абсолютной конфиденциальностью',
      whoTitle: 'Для кого',
      patients: 'Пациенты',
      patientsDesc: 'Мы помогаем организовать безопасный доступ к специализированным центрам здоровья с полной поддержкой от Кипра до прибытия',
      doctorsTitle: 'Врачи и клиники',
      doctorsDesc: 'Мы берём на себя административную и международную координацию сложных случаев',
      hospitalsTitle: 'Больницы и учреждения',
      hospitalsDesc: 'Через международные партнёрства с признанными организациями здравоохранения мы обеспечиваем доступ к специализированным услугам',
      ctaTitle: 'Нужно специализированное лечение за рубежом?',
      ctaDesc: 'Быстрый доступ к специалистам через наш координационный офис на Кипре',
      ctaConsultation: 'Запросить консультацию',
      ctaUpload: 'Отправить медицинские документы',
      ctaCall: 'Связаться с координатором',
      faqTitle: 'Часто задаваемые вопросы',
      faqSubtitle: 'Международный доступ к здравоохранению',
      featuredPartnerLabel: 'Партнёрский Медицинский Центр',
      holisticTagline: 'Медицинская помощь мирового уровня в Израиле',
      holisticDescription: 'Медицинский центр Sheba в Тель-ха-Шомер входит в число лучших больниц мира по версии Newsweek и признан на международном уровне в онкологии, кардиологии, трансплантации, репродуктивной медицине, неврологии и передовой хирургии. Через Sheba Global и при координации J.House of Secretary иностранные пациенты получают доступ к ведущим врачам, передовым исследованиям и полностью персонализированному, многоязычному сопровождению — от первого обращения до послеоперационного наблюдения.',
      holisticTags: ['Онкология', 'Кардиология', 'Трансплантация', 'Международные пациенты'],
      uploadModal: {
        title: 'Загрузить медицинские документы',
        subtitle: 'Отправьте свои медицинские отчеты и файлы безопасно',
        nameLabel: 'Полное имя',
        phoneLabel: 'Телефон',
        emailLabel: 'Email',
        messageLabel: 'Сообщение (необязательно)',
        messagePlaceholder: 'Кратко опишите ваш случай...',
        filesLabel: 'Файлы / Папки',
        filesHint: 'Принимаются все типы файлов • Максимум 25МБ всего',
        selectFiles: 'Выбрать файлы',
        selectFolder: 'Выбрать папку',
        clearFiles: 'Очистить',
        gdprConsent: 'Я согласен на обработку моих данных согласно GDPR для целей этого запроса.',
        submitBtn: 'Отправить',
        cancelBtn: 'Отмена',
        sending: 'Отправка...',
        successMsg: 'Ваш запрос успешно отправлен. Мы свяжемся с вами в ближайшее время.',
        errorMsg: 'Не удалось отправить. Повторите попытку или свяжитесь с нами напрямую.',
        sizeError: 'Общий размер файлов превышает лимит 25МБ.',
        requiredError: 'Пожалуйста, заполните все обязательные поля.',
        gdprError: 'Пожалуйста, примите согласие GDPR для продолжения.',
        rateLimitError: 'Слишком много запросов. Повторите попытку через {minutes} мин.',
        filesSelected: 'файл(ов) выбрано',
      },
      faqItems: [
        { q: 'Что такое услуга международного доступа к здравоохранению?', a: 'Это профессиональная координационная услуга для пациентов, ищущих второе мнение, специализированное лечение или доступ к международным центрам здоровья.' },
        { q: 'Вы предоставляете медицинские консультации?', a: 'Нет. Мы занимаемся административной и организационной координацией между пациентами и международными поставщиками медицинских услуг.' },
        { q: 'Как начинается процесс?', a: 'Просто свяжитесь с нами и отправьте основную информацию или результаты обследований.' },
        { q: 'Нужно ли мне ехать за границу?', a: 'Не всегда. Во многих случаях сначала может быть организована удалённая консультация.' },
        { q: 'Сколько стоит услуга?', a: 'Стоимость зависит от типа запроса. Информация предоставляется до начала процесса.' },
        { q: 'Как я могу начать сегодня?', a: 'Свяжитесь с нами по телефону, WhatsApp или через контактную форму на сайте.' },
      ],
      footer: 'Координация здравоохранения • Международный доступ • Профессиональная поддержка',
    },
    cookieConsent: {
      title: 'Мы используем cookies',
      message: 'Этот сайт использует cookies для наилучшего опыта работы. Мы используем только необходимые cookies для базовой функциональности и аналитики для улучшения наших услуг.',
      learnMore: 'Подробнее',
      acceptAll: 'Принять все',
      necessaryOnly: 'Только необходимые',
      policyTitle: 'Политика cookies',
      section1Title: 'Что такое cookies?',
      section1Body: 'Cookies — это небольшие текстовые файлы, сохраняемые на вашем устройстве при посещении сайта. Они помогают сайту работать правильно и обеспечивают лучший опыт просмотра.',
      section2Title: 'Как мы используем cookies',
      section2Body: 'Мы используем cookies для основной функциональности (сессии входа, языковые настройки) и для понимания того, как посетители взаимодействуют с нашим сайтом.',
      section3Title: 'Типы используемых cookies',
      cookieType1: 'Обязательные — для работы сайта (вход, язык, сессия)',
      cookieType2: 'Функциональные — запоминают ваши настройки',
      cookieType3: 'Аналитические — помогают понять использование сайта (анонимно)',
      section4Title: 'Ваши права (GDPR)',
      section4Body: 'Вы можете изменить или отозвать своё согласие в любое время, очистив данные браузера. У вас также есть право доступа, исправления или удаления ваших персональных данных в соответствии с GDPR ЕС.',
      contactTitle: 'Связаться с нами',
    },
  },
  ar: {
    sheba: { badge: 'الممثلون المعتمدون لمستشفى SHEBA' },
    splash: {
      back: 'رجوع',
      tagline: 'تنسيق الرعاية الصحية • الوصول الدولي • الدعم المهني',
      forDoctors: 'للأطباء العيادات المستشفيات',
      forPatients: 'للمرضى',
      internationalAccess: 'الوصول الدولي للرعاية الصحية',
      trustedBy: 'يثقون بنا',
      nightPharmacies: 'صيدليات الليل',
      nightPharmaciesDesc: 'ابحث عن أقرب صيدلية ليلية في منطقتك',
      nightPharmaciesFooter: 'يتم تحديث البيانات يومياً.',
      disclaimer1: 'خط المريض يقدم خدمات تسهيل ومساعدة المرضى في إيجاد أطباء متاحين وجدولة المواعيد.',
      disclaimer2: 'قد تقترح الخدمة أطباء شركاء متاحين بناءً على التوفر واحتياجات المريض.',
      disclaimer3: 'الاختيار النهائي للطبيب يتم حصرياً من قبل المريض.',
      disclaimer4: 'يشارك الأطباء الشركاء من خلال اشتراك سنوي للمشاركة في الشبكة وإدارة الطلبات.',
      disclaimer5: 'الخدمة لا تقدم استشارات طبية ولا تحل محل العلاقة بين الطبيب والمريض.',
    },
    philosophy: {
      eyebrow: 'فلسفتنا',
      etymology: 'كلمة «ασθενής» اليونانية (مريض) مشتقة من السابقة السلبية «α-» وكلمة «σθένος» (القوة) — وتشير إلى من يحتاج إلى الدعم والرعاية واستعادة القوة.',
      paragraphs: [
        'نؤمن بأن كل تواصل مع متخصص رعاية صحية هو فرصة لإعادة بعض من تلك القوة. لهذا السبب نولي اهتماماً خاصاً للمكالمة الأولى، حيث يمكن للأدب والفهم والاحتراف أن يصنع الفرق كله.',
        'في J.House of Secretary، لا نقوم فقط بإدارة المكالمات والمواعيد. نحن ندعم الناس في اللحظات التي يبحثون فيها عن التوجيه والرعاية والوصول إلى الخدمات الصحية المناسبة.',
        'من خلال خدمات الدعم السكرتاري وخط المريض والوصول الصحي الدولي، نضمن أن يشعر كل شخص بأنه مسموع ومفهوم وموجَّه باحترام ومسؤولية.',
        'من المكالمة الأولى إلى التواصل مع متخصصين في الرعاية الصحية في قبرص وخارجها، مهمتنا هي تحويل عدم اليقين إلى توجيه واضح، والعقبات إلى حلول، والقلق إلى شعور بالثقة.',
      ],
      closing: 'لأن خلف كل مكالمة ليس مجرد مريض، بل إنسان يستحق أن يشعر بأنه ليس وحده في رحلته نحو الصحة.',
    },
    nav: {
      home: 'الرئيسية',
      services: 'الخدمات',
      whyUs: 'لماذا نحن',
      testimonials: 'الشهادات',
      faq: 'الأسئلة الشائعة',
      contact: 'اتصل بنا',
      pricing: 'الأسعار',
    },
    hero: {
      tagline: 'شريكك الموثوق في خدمات السكرتارية المهنية',
      headline: 'لا تفوت أبداً مكالمة مريض مرة أخرى',
      title: 'مرحباً بكم في',
      subtitle: 'J.House of Secretary',
      description: 'شريكك الموثوق في خدمات السكرتارية الهاتفية المهنية والشركاتية والطبية، ملتزمون بتعزيز كفاءة أعمالك.',
      cta: 'تواصل معنا',
      ctaSecondary: 'استكشف الخدمات',
      ctaConsultation: 'احجز استشارة مجانية',
      available: 'متاح 24/7',
    },
    heroFeatures: {
      callAnswering: 'الرد على المكالمات 24/7',
      appointmentReminders: 'تذكيرات المواعيد',
      patientFollowUp: 'متابعة المرضى',
      telemedicineScheduling: 'جدولة الطب عن بُعد',
      patientIntake: 'استقبال المرضى',
      clinicWorkflow: 'دعم سير عمل العيادة',
    },
    services: {
      title: 'خدمات السكرتارية الهاتفية الشاملة',
      subtitle: 'بسّط عملياتك مع دعم سكرتاريتنا الهاتفية المتخصصة.',
      corporate: {
        title: 'دعم السكرتارية للشركات',
        description: 'تشمل خدماتنا إدارة المكالمات وجدولة المواعيد والدعم الإداري.',
      },
      medical: {
        title: 'خدمات السكرتارية الطبية',
        description: 'مصممة للمهنيين الطبيين، نضمن التعامل مع مكالمات المرضى بعناية.',
      },
      virtual: {
        title: 'خدمات المساعد الافتراضي',
        description: 'خدمات المساعد الافتراضي لدينا توفر دعماً مرناً وموثوقاً.',
      },
      pricing: {
        title: 'الأسعار',
        description: 'الأسعار حسب حجم مكالماتك.',
      },
      gesy: {
        title: 'GESY',
        description: 'نتعاون مع: العيادات الطبية الخاصة، مراكز التشخيص، المتخصصين في الرعاية الصحية، المؤسسات الطبية الكبرى في قبرص. تدعم خدماتنا الفرق الطبية في إدارة التواصل مع المرضى.',
      },
    },
    detailedServices: {
      title: 'خدماتنا',
      intro: 'في J House of Secretary نقدم خدمات دعم سكرتارية طبية متخصصة للعيادات الخاصة والمراكز الطبية.',
      phoneCalls: { title: 'إدارة مكالمات المرضى', items: ['الرد على المكالمات الواردة', 'تسجيل رسائل المرضى', 'تحويل المكالمات المهمة للطبيب', 'إدارة استفسارات المرضى', 'إعلام المرضى بساعات العمل'] },
      appointments: { title: 'إدارة المواعيد', items: ['حجز مواعيد جديدة', 'تغيير وإلغاء المواعيد', 'تنظيم جدول الطبيب', 'تجنب الحجوزات المزدوجة', 'تذكيرات المواعيد'] },
      emailManagement: { title: 'إدارة البريد الإلكتروني', items: ['مراقبة الرسائل الواردة', 'الرد على الطلبات الأساسية', 'تنظيم المراسلات', 'تحويل الرسائل المهمة'] },
      patientComm: { title: 'التواصل مع المرضى', items: ['إعلام المرضى بالمواعيد', 'متابعة بعد الزيارة', 'الإجابة على الأسئلة', 'إدارة الطلبات'] },
      patientIntake: { title: 'استقبال المرضى', items: ['تسجيل بيانات المرضى الجدد', 'جمع المعلومات قبل الموعد', 'تنظيم النماذج'] },
      adminSupport: { title: 'الدعم الإداري', items: ['تنظيم ملفات المرضى', 'إدارة المستندات', 'إعداد التقارير', 'الدعم اليومي'] },
      telemedicine: { title: 'دعم الطب عن بُعد', items: ['تنظيم الاستشارات عبر الإنترنت', 'إرسال روابط المواعيد', 'المساعدة التقنية'] },
      followUp: { title: 'متابعة المرضى', items: ['تذكيرات إعادة الفحص', 'متابعة بعد العلاج', 'معلومات المواعيد القادمة'] },
      clinicComm: { title: 'إدارة اتصالات العيادة', items: ['التنسيق بين الأطباء والمرضى', 'تنظيم الجدول اليومي', 'إدارة طلبات التعاون'] },
      newPatients: { title: 'دعم المرضى الجدد', items: ['معلومات عن الخدمات', 'إرشادات الزيارة الأولى', 'معلومات الفحوصات'] },
      medicalCenters: { title: 'دعم المراكز الطبية', items: ['التنسيق بين الأقسام', 'تنظيم العمليات اليومية', 'إدارة المرضى'] },
      remoteSupport: { title: 'الدعم عن بُعد', items: ['خدمات السكرتارية عن بُعد', 'تعاون مرن', 'تقليل التكاليف'] },
      gesyReferrals: { title: 'إدارة إحالات GESY', items: ['التحقق من صلاحية الإحالات', 'تتبع الموافقات والإخطار بأي مشاكل', 'تقارير لك أو لموظفيك', 'وفر الوقت وتأكد من صحة جميع الإحالات'] }
    },
    gdprSection: {
      title: 'الامتثال للتشريعات وحماية البيانات',
      description: 'خدماتنا متوافقة تماماً مع GDPR وتشريعات GESY، مما يضمن معالجة جميع بيانات المرضى بأمان وسرية.',
    },
    whyUs: {
      title: 'لماذا تختار J.House of Secretary؟',
      subtitle: 'اكتشف المزايا الفريدة للعمل معنا.',
      expertise: {
        title: 'الخبرة المهنية',
        description: 'فريقنا يتكون من محترفين ذوي خبرة.',
      },
      personalized: {
        title: 'النهج الشخصي',
        description: 'نفخر بقدرتنا على تخصيص خدماتنا.',
      },
      commitment: {
        title: 'الالتزام بالتميز',
        description: 'التزامنا الثابت بالتميز ورضا العملاء.',
      },
    },
    facilities: {
      title: 'مرافقنا الحديثة',
      subtitle: 'اكتشف مساحة مكتبنا الحديثة المصممة لخدمات السكرتارية الهاتفية المهنية.',
    },
    testimonials: {
      title: 'ماذا يقول عملاؤنا',
      subtitle: 'استمع إلى الشركات التي تثق بنا.',
      items: [
        { name: 'د. أندرياس ك.', role: 'مدير العيادة', text: 'J.House of Secretary غيّرت طريقة تعاملنا مع مكالمات المرضى.' },
        { name: 'ماريا س.', role: 'صاحبة عمل', text: 'خدمة السكرتارية للشركات كانت لا تقدر بثمن.' },
        { name: 'كريستوس ب.', role: 'شريك قانوني', text: 'خدمة ممتازة. الدعم متعدد اللغات استثنائي.' },
      ],
    },
    faq: {
      title: 'الأسئلة الشائعة',
      subtitle: 'اعثر على إجابات للأسئلة الشائعة.',
      items: [
        { question: 'ما الخدمات التي تقدمونها؟', answer: 'نقدم خدمات سكرتارية هاتفية شاملة.' },
        { question: 'هل خدماتكم متاحة 24/7؟', answer: 'نعم، فريقنا متاح على مدار الساعة.' },
        { question: 'ما اللغات التي تدعمونها؟', answer: 'اليونانية والإنجليزية والروسية والعربية والصينية والفرنسية والإسبانية.' },
        { question: 'كيف يتم تحديد الأسعار؟', answer: 'الأسعار تعتمد على حجم المكالمات.' },
        { question: 'كيف أبدأ؟', answer: 'ببساطة تواصل معنا عبر الهاتف أو البريد الإلكتروني.' },
      ],
    },
    contact: {
      title: 'اتصل بنا',
      subtitle: 'تواصل مع فريقنا للحصول على استشارة شخصية.',
      form: {
        name: 'اسمك',
        email: 'البريد الإلكتروني',
        phone: 'رقم الهاتف',
        message: 'رسالتك',
        submit: 'إرسال',
        sending: 'جاري الإرسال...',
        success: 'تم إرسال الرسالة بنجاح!',
        error: 'فشل الإرسال. حاول مرة أخرى.',
      },
      info: {
        phone: 'الهاتف',
        mobile: 'الجوال',
        email: 'البريد',
        address: 'العنوان',
        addressLine: 'أفروديتيس 10، نيقوسيا 1060، قبرص',
      },
    },
    chat: {
      title: 'الدردشة الحية',
      placeholder: 'اكتب رسالتك...',
      welcome: 'مرحباً! كيف يمكننا مساعدتك اليوم؟',
      offline: 'نحن حالياً غير متصلين. اترك لنا رسالة.',
    },
    footer: {
      rights: 'جميع الحقوق محفوظة',
      privacy: 'سياسة الخصوصية',
      terms: 'الشروط والأحكام',
    },
    partners: {
      title: 'شركاؤنا',
      subtitle: 'موثوق به من قبل المؤسسات الصحية الرائدة في قبرص',
    },
    patientLine: {
      back: 'رجوع',
      phone: '7000 0223',
      tagline: 'الخدمة المبتكرة الجديدة من J.House of Secretary',
      title: 'خط المريض',
      slogan: '"صحتك، خطوة للأمام."',
      free: 'مجاني لجميع المرضى — بدون أي رسوم.',
      mainMessage: 'نهاية معاناة المريض.',
      subMessage: 'أنت تتصل — نحن نتولى الأمر.',
      servicesTitle: 'ما نقوم به من أجلك',
      services: [
        'حجز موعد مع التخصص الذي تريده',
        'الوصول إلى مراكز الأشعة والتحاليل',
        'إيجاد أقرب صيدلية',
        'تذكيرات المواعيد',
        'المتابعة بعد الفحص'
      ],
      stepsTitle: 'خطوات للمرضى',
      stepsSubtitle: '4 خطوات فقط لحل أي حاجة صحية',
      steps: [
        { number: '01', title: 'اتصل بخط المريض', description: 'اتصل على 7000 0223 / 801 101 0364 وتحدث مع فريقنا المتخصص.' },
        { number: '02', title: 'أخبرنا بما تحتاج', description: 'موعد مع تخصص، فحص، الوصول لمركز تشخيصي أو صيدلية.' },
        { number: '03', title: 'نحن نتولى كل شيء', description: 'نحجز المواعيد، نتحقق من الإحالات، نجد أقرب نقطة خدمة.' },
        { number: '04', title: 'تستلم التأكيد', description: 'نُعلمك بالتاريخ والوقت ومكان موعدك.' }
      ],
      benefitsTitle: 'نهاية التوتر',
      benefitsSubtitle: 'لماذا تختار خط المريض',
      benefits: [
        { title: 'هاتف واحد لكل شيء', description: 'اتصل فقط على 7000 0223 / 801 101 0364 لأي حاجة صحية.' },
        { title: 'توفير الوقت', description: 'لا مزيد من الانتظار والمكالمات اللانهائية.' },
        { title: 'مجاني 100%', description: 'بدون أي رسوم على المرضى.' },
        { title: 'رعاية شخصية', description: 'خدمة مخصصة لكل مريض.' },
        { title: 'متوافق مع GDPR', description: 'حماية مطلقة لبياناتك الشخصية.' }
      ],
      ctaTitle: 'اتصل الآن',
      ctaSubtitle: 'متاحون يومياً لخدمتك',
      compatible: 'متوافق مع',
    },
    international: {
      back: 'رجوع',
      pageTitle: 'الوصول الدولي للرعاية الصحية',
      subtitle: 'تنسيق للمرضى الباحثين عن حلول متخصصة في الخارج',
      description: 'نتولى التنسيق المهني للمرضى الباحثين عن خدمات طبية متقدمة أو رأي ثانٍ أو علاج في مراكز صحية دولية معترف بها. بسرية تامة وتوجيه صحيح ودعم شخصي في كل مرحلة.',
      missionQuote: '"Helping healthcare work better — حلول رعاية متخصصة عبر الحدود"',
      servicesTitle: 'خدمات التنسيق الدولي',
      service1Title: 'تنسيق الحالات الطبية في الخارج',
      service1Desc: 'تنسيق كامل للعلاج في مراكز دولية معترف بها',
      service2Title: 'رأي طبي ثانٍ',
      service2Desc: 'الوصول إلى أطباء متخصصين من مؤسسات رائدة',
      service3Title: 'دعم الملف الطبي والتواصل',
      service3Desc: 'إدارة كاملة للوثائق والترجمات والمراسلات',
      service4Title: 'تنظيم المواعيد والإجراءات',
      service4Desc: 'حجز المواعيد وتحضير الفحوصات واللوجستيات',
      service5Title: 'توجيه شخصي للمرضى',
      service5Desc: 'دعم فردي في كل مرحلة من العملية',
      service6Title: 'Premium Patient Assistance',
      service6Desc: 'خدمات كونسيرج لمرضى VIP بسرية تامة',
      whoTitle: 'لمن هذه الخدمة',
      patients: 'المرضى',
      patientsDesc: 'نساعدك في تنظيم وصول آمن إلى مراكز صحية متخصصة مع دعم كامل من قبرص حتى وصولك',
      doctorsTitle: 'الأطباء والعيادات',
      doctorsDesc: 'نتولى التنسيق الإداري والدولي للحالات المعقدة',
      hospitalsTitle: 'المستشفيات والمؤسسات',
      hospitalsDesc: 'من خلال شراكات دولية مع منظمات صحية معترف بها نوفر الوصول إلى خدمات متخصصة',
      ctaTitle: 'تحتاج علاجاً متخصصاً في الخارج؟',
      ctaDesc: 'وصول سريع للمتخصصين عبر مكتب التنسيق في قبرص',
      ctaConsultation: 'طلب استشارة',
      ctaUpload: 'إرسال التقارير الطبية',
      ctaCall: 'التحدث مع المنسق',
      faqTitle: 'الأسئلة الشائعة',
      faqSubtitle: 'الوصول الدولي للرعاية الصحية',
      featuredPartnerLabel: 'مركز طبي شريك',
      holisticTagline: 'رعاية طبية عالمية المستوى في إسرائيل',
      holisticDescription: 'يُصنَّف مركز شيبا الطبي في تل هَشومير باستمرار ضمن أفضل مستشفيات العالم وفقاً لمجلة Newsweek، ويحظى بمكانة دولية مرموقة في مجالات الأورام وأمراض القلب وزراعة الأعضاء والخصوبة والأعصاب والجراحة المتقدمة. من خلال Sheba Global وبالتنسيق مع J.House of Secretary، يحصل المرضى الدوليون على أفضل الأطباء والأبحاث المتقدمة وإدارة حالة شخصية متعددة اللغات — من الاستفسار الأول وحتى المتابعة بعد العلاج.',
      holisticTags: ['الأورام', 'أمراض القلب', 'زراعة الأعضاء', 'المرضى الدوليون'],
      uploadModal: {
        title: 'رفع المستندات الطبية',
        subtitle: 'أرسل تقاريرك الطبية وملفاتك بأمان',
        nameLabel: 'الاسم الكامل',
        phoneLabel: 'الهاتف',
        emailLabel: 'البريد الإلكتروني',
        messageLabel: 'الرسالة (اختياري)',
        messagePlaceholder: 'صف حالتك بإيجاز...',
        filesLabel: 'الملفات / المجلدات',
        filesHint: 'يتم قبول جميع أنواع الملفات • الحد الأقصى 25 ميجابايت إجمالاً',
        selectFiles: 'اختر الملفات',
        selectFolder: 'اختر مجلداً',
        clearFiles: 'مسح',
        gdprConsent: 'أوافق على معالجة بياناتي وفقاً للائحة GDPR لأغراض هذا الطلب.',
        submitBtn: 'إرسال',
        cancelBtn: 'إلغاء',
        sending: 'جاري الإرسال...',
        successMsg: 'تم إرسال طلبك بنجاح. سنتواصل معك قريباً.',
        errorMsg: 'فشل الإرسال. يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.',
        sizeError: 'الحجم الإجمالي للملفات يتجاوز حد 25 ميجابايت.',
        requiredError: 'يرجى ملء جميع الحقول المطلوبة.',
        gdprError: 'يرجى قبول موافقة GDPR للمتابعة.',
        rateLimitError: 'طلبات كثيرة جداً. يرجى المحاولة مرة أخرى خلال {minutes} دقيقة.',
        filesSelected: 'ملف محدد',
      },
      faqItems: [
        { q: 'ما هي خدمة الوصول الدولي للرعاية الصحية؟', a: 'هي خدمة تنسيق مهنية للمرضى الباحثين عن رأي ثانٍ أو علاج متخصص أو الوصول إلى مراكز صحية دولية معترف بها.' },
        { q: 'هل تقدمون استشارات طبية؟', a: 'لا. نحن لا نقدم تشخيصاً أو علاجاً طبياً. نحن نتولى التنسيق الإداري والتنظيمي بين المرضى ومقدمي الرعاية الصحية الدوليين.' },
        { q: 'كيف تبدأ العملية؟', a: 'ما عليك سوى الاتصال بنا وإرسال المعلومات الأساسية أو الفحوصات المتاحة، حتى نتمكن من تقييم مسار التنسيق الأنسب.' },
        { q: 'هل أحتاج إلى السفر إلى الخارج؟', a: 'ليس دائماً. في كثير من الحالات، يمكن تنظيم رأي ثانٍ أو تقييم عن بُعد في البداية.' },
        { q: 'هل يمكنني إرسال الفحوصات إلكترونياً؟', a: 'نعم. يمكنك إرسال الفحوصات والتقارير والوثائق الطبية بشكل آمن إلكترونياً.' },
        { q: 'في أي دول يمكنكم المساعدة؟', a: 'وفقاً للحالة وتوفر المنظمات الشريكة، نوفر الوصول إلى مراكز صحية دولية مختارة.' },
        { q: 'ما مدى سرعة تقدم الطلب؟', a: 'يختلف الوقت حسب الحالة، ولكننا نعطي الأولوية للحالات العاجلة والخطيرة بتنسيق فوري.' },
        { q: 'هل تقدمون خدمات باللغة اليونانية؟', a: 'نعم. نقدم دعماً كاملاً باليونانية، وعند الحاجة بالإنجليزية.' },
        { q: 'ما أنواع الحالات التي تتعاملون معها؟', a: 'ندعم حالات مثل الرأي الثاني والحالات المعقدة والعلاجات المتخصصة والأورام وأمراض القلب وطب الأطفال والأعصاب وغيرها.' },
        { q: 'هل تتولون أيضاً المواعيد؟', a: 'نعم. يمكننا المساعدة في جدولة المواعيد والإجراءات والخطوات ذات الصلة.' },
        { q: 'هل تقدمون الدعم أثناء العلاج؟', a: 'نعم. نبقى متاحين للتنسيق الإداري والتوجيه في كل مرحلة.' },
        { q: 'ماذا عن سرية بياناتي الشخصية؟', a: 'يتم التعامل مع جميع المعلومات بمهنية وتكتم واحترام للخصوصية.' },
        { q: 'كم تكلفة الخدمة؟', a: 'تعتمد التكلفة على نوع الطلب ومتطلبات التنسيق. يتم تقديم معلومات مفصلة قبل البدء.' },
        { q: 'هل يمكنني طلب رأي ثانٍ فقط؟', a: 'بالتأكيد. يطلب العديد من المرضى في البداية رأياً ثانياً قبل تحديد الخطوات التالية.' },
        { q: 'هل تتعاونون مع الأطباء والعيادات؟', a: 'نعم. ندعم أيضاً المهنيين الصحيين والعيادات والمنظمات التي تحتاج إلى تنسيق دولي للحالات.' },
        { q: 'هل تخدمون جميع أنحاء قبرص؟', a: 'نعم. نقدم خدمة في جميع أنحاء قبرص مع إمكانية التواصل عن بُعد.' },
        { q: 'هل يمكنني التواصل عبر WhatsApp؟', a: 'نعم. التواصل المباشر متاح عبر الهاتف و WhatsApp.' },
        { q: 'ما هو دوركم في العملية؟', a: 'دورنا هو تبسيط عملية معقدة والعمل كنقطة تنسيق بين المريض والجهات الصحية الدولية.' },
        { q: 'ماذا يحدث في حالات الطوارئ؟', a: 'يمكنك الاتصال بنا فوراً لتقييم أسرع طريقة ممكنة للدعم.' },
        { q: 'كيف يمكنني البدء اليوم؟', a: 'يمكنك الاتصال بنا عبر الهاتف أو WhatsApp أو من خلال نموذج الاتصال في موقعنا.' },
      ],
      footer: 'تنسيق الرعاية الصحية • الوصول الدولي • الدعم المهني',
    },
    cookieConsent: {
      title: 'نحن نستخدم ملفات تعريف الارتباط',
      message: 'يستخدم هذا الموقع ملفات تعريف الارتباط لضمان أفضل تجربة. نستخدم فقط ملفات تعريف الارتباط الضرورية للوظائف الأساسية والتحليلات لتحسين خدماتنا.',
      learnMore: 'معرفة المزيد',
      acceptAll: 'قبول الكل',
      necessaryOnly: 'الضرورية فقط',
      policyTitle: 'سياسة ملفات تعريف الارتباط',
      section1Title: 'ما هي ملفات تعريف الارتباط؟',
      section1Body: 'ملفات تعريف الارتباط هي ملفات نصية صغيرة تُخزن على جهازك عند زيارة الموقع. تساعد على عمل الموقع بشكل صحيح وتوفر تجربة تصفح أفضل.',
      section2Title: 'كيف نستخدم ملفات تعريف الارتباط',
      section2Body: 'نستخدم ملفات تعريف الارتباط للوظائف الأساسية (جلسات تسجيل الدخول، تفضيلات اللغة) ولفهم كيفية تفاعل الزوار مع موقعنا.',
      section3Title: 'أنواع ملفات تعريف الارتباط',
      cookieType1: 'الأساسية — مطلوبة لعمل الموقع',
      cookieType2: 'الوظيفية — تذكر تفضيلاتك',
      cookieType3: 'التحليلية — تساعدنا على فهم استخدام الموقع (مجهول)',
      section4Title: 'حقوقك (GDPR)',
      section4Body: 'يمكنك تغيير أو سحب موافقتك في أي وقت عن طريق مسح بيانات المتصفح. لديك أيضاً الحق في الوصول إلى بياناتك الشخصية أو تصحيحها أو حذفها وفقاً للائحة GDPR.',
      contactTitle: 'تواصل معنا',
    },
  },
  zh: {
    sheba: { badge: 'SHEBA 授权代表' },
    splash: {
      back: '返回',
      tagline: '医疗协调 • 国际通道 • 专业支持',
      forDoctors: '面向医生 诊所 医院',
      forPatients: '面向患者',
      internationalAccess: '国际医疗通道',
      trustedBy: '信赖我们',
      nightPharmacies: '夜间药房',
      nightPharmaciesDesc: '查找您附近最近的夜间药房',
      nightPharmaciesFooter: '数据每日更新。',
      disclaimer1: '患者热线提供便利服务，帮助患者找到可用医生并安排预约。',
      disclaimer2: '该服务可能根据可用性和患者需求推荐合作医生，但不影响医疗决策。',
      disclaimer3: '最终的医生选择完全由患者做出。',
      disclaimer4: '合作医生通过年度订阅参与网络和请求管理。',
      disclaimer5: '该服务不提供医疗建议，也不替代医患关系。',
    },
    philosophy: {
      eyebrow: '我们的理念',
      etymology: '希腊语「ασθενής」（病患）一词，源自否定前缀「α-」与「σθένος」（力量）——意指那些需要支持、关怀与重拾力量的人。',
      paragraphs: [
        '我们相信，每一次与医疗专业人员的沟通，都是为他们重新注入一份力量的机会。正因如此，我们格外珍视第一通电话——在那里，礼貌、理解与专业，足以带来全然不同的改变。',
        '在 J.House of Secretary，我们不仅仅是接听电话与安排预约。我们陪伴人们走过那些寻求指引、关怀与适切医疗服务的时刻。',
        '透过我们的秘书支援服务、病患专线与国际医疗服务，我们让每一位来电者都能感受到被聆听、被理解，并以尊重与责任感被妥善引导。',
        '从第一通电话，到联系塞浦路斯与海外的专业医疗团队，我们的使命是将不确定转化为清晰的指引，将障碍化为解决方案，将焦虑化为信心。',
      ],
      closing: '因为每一通电话背后，不只是一位病患，而是一个值得在通往健康的道路上不再孤单的人。',
    },
    nav: {
      home: '首页',
      services: '服务',
      whyUs: '为何选择我们',
      testimonials: '客户评价',
      faq: '常见问题',
      contact: '联系我们',
      pricing: '价格',
    },
    hero: {
      tagline: '您专业秘书服务的可信赖合作伙伴',
      headline: '永不再错过患者来电',
      title: '欢迎来到',
      subtitle: 'J.House of Secretary',
      description: '您在专业、企业和医疗电话秘书服务方面的可信赖合作伙伴，致力于提升您的业务效率。',
      cta: '联系我们',
      ctaSecondary: '探索服务',
      ctaConsultation: '预约免费咨询',
      available: '24/7 全天候服务',
    },
    heroFeatures: {
      callAnswering: '24/7电话接听',
      appointmentReminders: '预约提醒',
      patientFollowUp: '患者跟进',
      telemedicineScheduling: '远程医疗安排',
      patientIntake: '患者接待',
      clinicWorkflow: '诊所工作流程支持',
    },
    services: {
      title: '全面的电话秘书服务',
      subtitle: '通过我们专业电话秘书的支持，简化您的运营。',
      corporate: {
        title: '企业电话秘书支持',
        description: '我们的企业电话秘书服务包括通话管理、预约安排和行政支持。',
      },
      medical: {
        title: '医疗电话秘书服务',
        description: '专为医疗专业人员设计，确保患者来电得到妥善处理。',
      },
      virtual: {
        title: '虚拟助理服务',
        description: '我们的虚拟助理服务提供灵活可靠的支持。',
      },
      pricing: {
        title: '价格',
        description: '根据您的通话量定价。',
      },
      gesy: {
        title: 'GESY',
        description: '我们与以下机构合作：私人医疗诊所、诊断中心、医疗专业人员、塞浦路斯主要医疗机构。我们的服务支持医疗团队管理患者沟通和预约。',
      },
    },
    detailedServices: {
      title: '我们的服务',
      intro: '在J House of Secretary，我们为私人诊所、诊所和医疗中心提供专业的医疗秘书支持服务。',
      phoneCalls: { title: '患者电话管理', items: ['接听来电', '记录患者留言', '转接重要电话给医生', '处理患者咨询', '告知营业时间'] },
      appointments: { title: '预约管理', items: ['预约新诊', '更改和取消预约', '组织医生日程', '避免重复预约', '预约提醒'] },
      emailManagement: { title: '邮件管理', items: ['监控收件箱', '回复基本请求', '组织专业通信', '转发重要信息'] },
      patientComm: { title: '患者沟通', items: ['通知患者预约', '就诊后跟进', '回答基本问题', '处理新患者请求'] },
      patientIntake: { title: '患者接待', items: ['登记新患者信息', '预约前收集信息', '组织患者表格'] },
      adminSupport: { title: '行政支持', items: ['组织患者档案', '文件管理', '准备报告', '日常行政支持'] },
      telemedicine: { title: '远程医疗支持', items: ['组织在线咨询', '发送在线预约链接', '技术帮助'] },
      followUp: { title: '患者跟进', items: ['复查提醒', '治疗后跟进', '下次预约信息'] },
      clinicComm: { title: '诊所沟通管理', items: ['医患协调', '组织日程', '管理合作请求'] },
      newPatients: { title: '新患者支持', items: ['服务信息', '首次就诊指导', '检查基本信息'] },
      medicalCenters: { title: '医疗中心支持', items: ['部门协调', '组织日常运营', '患者管理'] },
      remoteSupport: { title: '远程秘书支持', items: ['远程秘书服务', '灵活合作', '降低人员成本'] },
      gesyReferrals: { title: 'GESY转诊管理', items: ['检查转诊有效性', '跟踪批准并通知任何问题', '为您或员工提供报告', '节省时间并确保所有转诊正确就绪'] }
    },
    gdprSection: {
      title: '法规合规与数据保护',
      description: '我们的服务完全符合GDPR和GESY法规，确保所有患者数据得到安全和保密处理。',
    },
    whyUs: {
      title: '为何选择 J.House of Secretary？',
      subtitle: '发现与我们合作的独特优势。',
      expertise: {
        title: '专业知识',
        description: '我们的团队由经验丰富的专业人员组成。',
      },
      personalized: {
        title: '个性化方法',
        description: '我们以能够根据客户需求定制服务为荣。',
      },
      commitment: {
        title: '卓越承诺',
        description: '我们对卓越和客户满意度的坚定承诺。',
      },
    },
    facilities: {
      title: '我们的现代设施',
      subtitle: '探索我们为专业电话秘书服务设计的先进办公空间。',
    },
    testimonials: {
      title: '客户评价',
      subtitle: '听听信任我们的企业怎么说。',
      items: [
        { name: 'Andreas K. 医生', role: '诊所主任', text: 'J.House of Secretary 改变了我们处理患者来电的方式。' },
        { name: 'Maria S.', role: '企业主', text: '企业电话秘书服务非常宝贵。' },
        { name: 'Christos P.', role: '律所合伙人', text: '优秀的服务。多语言支持非常出色。' },
      ],
    },
    faq: {
      title: '常见问题',
      subtitle: '查找有关我们服务的常见问题答案。',
      items: [
        { question: '你们提供哪些服务？', answer: '我们提供全面的电话秘书服务。' },
        { question: '你们的服务是否24/7可用？', answer: '是的，我们的团队全天候可用。' },
        { question: '你们支持哪些语言？', answer: '希腊语、英语、俄语、阿拉伯语、中文、法语和西班牙语。' },
        { question: '如何确定定价？', answer: '定价基于通话量。' },
        { question: '如何开始？', answer: '只需通过电话或电子邮件联系我们。' },
      ],
    },
    contact: {
      title: '联系我们',
      subtitle: '与我们的团队联系，获取个性化咨询。',
      form: {
        name: '您的姓名',
        email: '电子邮件',
        phone: '电话号码',
        message: '您的留言',
        submit: '发送消息',
        sending: '发送中...',
        success: '消息发送成功！',
        error: '发送失败，请重试。',
      },
      info: {
        phone: '电话',
        mobile: '手机',
        email: '邮箱',
        address: '地址',
        addressLine: 'Afroditis 10, 尼科西亚 1060, 塞浦路斯',
      },
    },
    chat: {
      title: '在线聊天',
      placeholder: '输入您的消息...',
      welcome: '您好！我们今天能为您提供什么帮助？',
      offline: '我们目前离线。请留言。',
    },
    footer: {
      rights: '版权所有',
      privacy: '隐私政策',
      terms: '服务条款',
    },
    partners: {
      title: '我们的合作伙伴',
      subtitle: '受到塞浦路斯领先医疗机构的信赖',
    },
    patientLine: {
      back: '返回',
      phone: '7000 0223',
      tagline: 'J.House of Secretary 的创新服务',
      title: '患者热线',
      slogan: '"您的健康，领先一步。"',
      free: '对所有患者免费 — 无任何费用。',
      mainMessage: '终结患者的困扰。',
      subMessage: '您拨打电话 — 我们来处理。',
      servicesTitle: '我们为您提供的服务',
      services: [
        '预约您需要的专科医生',
        '访问放射和血液检测中心',
        '查找最近的药房',
        '预约提醒',
        '检查后跟进'
      ],
      stepsTitle: '患者步骤',
      stepsSubtitle: '只需4步解决任何健康需求',
      steps: [
        { number: '01', title: '拨打患者热线', description: '拨打7000 0223 / 801 101 0364，与我们的专业团队交流。' },
        { number: '02', title: '告诉我们您的需求', description: '专科预约、检查、诊断中心或药房。' },
        { number: '03', title: '我们处理一切', description: '我们预约、核实转诊单、找到最近的服务点。' },
        { number: '04', title: '收到确认', description: '我们会通知您预约的日期、时间和地点。' }
      ],
      benefitsTitle: '告别焦虑',
      benefitsSubtitle: '为什么选择患者热线',
      benefits: [
        { title: '一个电话搞定一切', description: '只需拨打7000 0223 / 801 101 0364满足任何健康需求。' },
        { title: '节省时间', description: '不再等待和无休止的电话。' },
        { title: '100%免费', description: '对患者无任何收费。' },
        { title: '个性化关怀', description: '为每位患者提供个性化服务。' },
        { title: 'GESY兼容', description: '完全符合通用医疗保健系统。' },
        { title: 'GDPR合规', description: '绝对保护您的个人数据。' }
      ],
      ctaTitle: '立即拨打',
      ctaSubtitle: '每天为您服务',
      compatible: '兼容',
    },
    international: {
      back: '返回',
      pageTitle: '国际医疗通道',
      subtitle: '为寻求海外专业解决方案的患者提供协调服务',
      description: '我们为寻求先进医疗服务、第二意见或在国际知名健康中心接受治疗的患者提供专业协调。绝对保密、正确指导和个性化支持。',
      missionQuote: '"Helping healthcare work better — 跨越国界的专业护理解决方案"',
      servicesTitle: '国际协调服务',
      service1Title: '海外医疗案例协调',
      service1Desc: '在国际知名中心进行全面的治疗协调',
      service2Title: '第二医疗意见',
      service2Desc: '获得顶级机构专科医生的咨询',
      service3Title: '医疗档案和沟通支持',
      service3Desc: '完整的文件管理、翻译和通信',
      service4Title: '预约和程序组织',
      service4Desc: '预约、检查准备、后勤保障',
      service5Title: '个性化患者指导',
      service5Desc: '在流程的每个阶段提供个性化支持',
      service6Title: 'Premium Patient Assistance',
      service6Desc: '为VIP患者提供绝对保密的礼宾服务',
      whoTitle: '适用对象',
      patients: '患者',
      patientsDesc: '我们帮助您安全地获得专业健康中心的服务，从塞浦路斯到您的目的地提供全程支持',
      doctorsTitle: '医生和诊所',
      doctorsDesc: '我们负责复杂案例的行政和国际协调',
      hospitalsTitle: '医院和机构',
      hospitalsDesc: '通过与国际知名卫生组织的合作，我们提供专业服务的通道',
      ctaTitle: '需要海外专业治疗？',
      ctaDesc: '通过我们在塞浦路斯的协调办公室快速获得专家服务',
      ctaConsultation: '申请咨询',
      ctaUpload: '上传医疗报告',
      ctaCall: '联系协调员',
      faqTitle: '常见问题',
      faqSubtitle: '国际医疗通道',
      featuredPartnerLabel: '合作医疗中心',
      holisticTagline: '以色列世界级医疗服务',
      holisticDescription: '位于特拉哈绍梅尔的谢巴医疗中心（Sheba Medical Center）常年被《新闻周刊》评为全球顶尖医院，在肿瘤学、心脏病学、器官移植、生殖医学、神经科学及先进外科领域享有国际盛誉。通过 Sheba Global 与 J.House of Secretary 协同，国际患者可获得世界一流的医生资源、前沿的科研成果，以及从初次咨询到术后随访的全流程、个性化、多语种病例管理。',
      holisticTags: ['肿瘤学', '心脏病学', '器官移植', '国际患者'],
      uploadModal: {
        title: '上传医疗文件',
        subtitle: '安全地发送您的医疗报告和文件',
        nameLabel: '全名',
        phoneLabel: '电话',
        emailLabel: '电子邮件',
        messageLabel: '留言（可选）',
        messagePlaceholder: '简要描述您的情况...',
        filesLabel: '文件 / 文件夹',
        filesHint: '接受所有文件类型 • 最多25MB总计',
        selectFiles: '选择文件',
        selectFolder: '选择文件夹',
        clearFiles: '清除',
        gdprConsent: '我同意根据GDPR处理我的数据，用于本次请求的目的。',
        submitBtn: '发送',
        cancelBtn: '取消',
        sending: '发送中...',
        successMsg: '您的请求已成功发送。我们将尽快与您联系。',
        errorMsg: '发送失败。请重试或直接联系我们。',
        sizeError: '总文件大小超过25MB限制。',
        requiredError: '请填写所有必填字段。',
        gdprError: '请接受GDPR同意以继续。',
        rateLimitError: '请求过多。请在 {minutes} 分钟后重试。',
        filesSelected: '已选文件',
      },
      faqItems: [
        { q: '什么是国际医疗通道服务？', a: '这是一项为寻求第二医疗意见、专业治疗或访问国际知名健康中心的患者提供的专业协调服务。' },
        { q: '你们提供医疗建议吗？', a: '不。我们不提供医疗诊断或治疗。我们负责患者与国际医疗服务提供者之间的行政和组织协调。' },
        { q: '流程如何开始？', a: '只需联系我们并发送基本信息或现有检查，我们将评估最合适的协调路径。' },
        { q: '我需要出国吗？', a: '并非总是。在许多情况下，可以先安排第二意见或远程评估。' },
        { q: '我可以电子方式发送检查吗？', a: '是的。您可以安全地通过电子方式发送检查、报告和医疗文件。' },
        { q: '你们可以在哪些国家提供帮助？', a: '根据案例和合作组织的可用性，我们提供对选定国际健康中心的访问。' },
        { q: '请求可以多快处理？', a: '时间因案例而异，但我们优先处理紧急和严重的案例，并立即协调。' },
        { q: '你们提供希腊语服务吗？', a: '是的。我们提供希腊语的全面支持，必要时也提供英语支持。' },
        { q: '你们处理哪些类型的案例？', a: '我们支持第二意见、复杂案例、专业治疗、肿瘤学、心脏病学、儿科、神经病学等案例。' },
        { q: '你们也负责预约吗？', a: '是的。我们可以帮助安排预约、程序和相关步骤。' },
        { q: '治疗期间你们提供支持吗？', a: '是的。我们在每个阶段提供行政协调和指导。' },
        { q: '我的个人数据保密性如何？', a: '所有信息都以专业、谨慎和尊重隐私的方式处理。' },
        { q: '服务费用是多少？', a: '费用取决于请求类型和协调要求。开始前会提供详细信息。' },
        { q: '我可以只寻求第二意见吗？', a: '当然。许多患者在决定下一步之前首先寻求第二意见。' },
        { q: '你们与医生和诊所合作吗？', a: '是的。我们还支持需要国际案例协调的医疗专业人员、诊所和组织。' },
        { q: '你们服务整个塞浦路斯吗？', a: '是的。我们提供全塞浦路斯服务，可进行远程沟通。' },
        { q: '我可以通过WhatsApp联系吗？', a: '是的。可通过电话和WhatsApp直接沟通。' },
        { q: '你们在流程中的角色是什么？', a: '我们的角色是简化复杂的流程，作为患者与国际医疗机构之间的协调点。' },
        { q: '紧急情况下怎么办？', a: '您可以立即联系我们，以评估最快的支持方式。' },
        { q: '我今天如何开始？', a: '您可以通过电话、WhatsApp或我们网站的联系表格与我们联系。' },
      ],
      footer: '医疗协调 • 国际通道 • 专业支持',
    },
    cookieConsent: {
      title: '我们使用 cookies',
      message: '本网站使用 cookies 以确保您获得最佳体验。我们仅使用必要的 cookies 用于基本功能和分析，以改进我们的服务。',
      learnMore: '了解更多',
      acceptAll: '接受全部',
      necessaryOnly: '仅必要的',
      policyTitle: 'Cookie 政策',
      section1Title: '什么是 cookies？',
      section1Body: 'Cookies 是访问网站时存储在您设备上的小型文本文件。它们帮助网站正常运行并提供更好的浏览体验。',
      section2Title: '我们如何使用 cookies',
      section2Body: '我们使用 cookies 用于基本网站功能（登录会话、语言偏好）以及了解访客如何与我们的网站互动。',
      section3Title: '我们使用的 cookie 类型',
      cookieType1: '必要 cookies — 网站运行所需',
      cookieType2: '功能性 cookies — 记住您的偏好',
      cookieType3: '分析 cookies — 帮助我们了解网站使用情况（匿名）',
      section4Title: '您的权利 (GDPR)',
      section4Body: '您可以随时通过清除浏览器数据来更改或撤回您的同意。根据欧盟 GDPR，您还有权访问、更正或删除您的个人数据。',
      contactTitle: '联系我们',
    },
  },
  fr: {
    sheba: { badge: 'REPRÉSENTANTS AUTORISÉS DE SHEBA' },
    splash: {
      back: 'Retour',
      tagline: 'Coordination Santé • Accès International • Support Professionnel',
      forDoctors: 'Pour Médecins Cliniques Hôpitaux',
      forPatients: 'Pour Patients',
      internationalAccess: 'Accès International à la Santé',
      trustedBy: 'ILS NOUS FONT CONFIANCE',
      nightPharmacies: 'PHARMACIES DE NUIT',
      nightPharmaciesDesc: 'Trouvez la pharmacie de nuit la plus proche',
      nightPharmaciesFooter: 'Les données sont mises à jour quotidiennement.',
      disclaimer1: 'La Ligne Patient fournit des services de facilitation pour trouver des médecins disponibles et planifier des rendez-vous.',
      disclaimer2: 'Le service peut suggérer des médecins partenaires disponibles selon les besoins du patient.',
      disclaimer3: 'Le choix final du médecin est fait exclusivement par le patient.',
      disclaimer4: 'Les médecins partenaires participent via un abonnement annuel pour la participation au réseau.',
      disclaimer5: 'Le service ne fournit pas de conseils médicaux ni ne remplace la relation médecin-patient.',
    },
    philosophy: {
      eyebrow: 'NOTRE PHILOSOPHIE',
      etymology: 'Le mot grec « ασθενής » (patient) vient du préfixe privatif « α- » et de « σθένος » (la force) — désignant celui qui a besoin de soutien, de soin et de force retrouvée.',
      paragraphs: [
        'Nous croyons que chaque échange avec un professionnel de santé est une occasion de restituer un peu de cette force. C\u2019est pourquoi nous accordons une attention toute particulière au premier appel — là où la courtoisie, l\u2019écoute et le professionnalisme peuvent faire toute la différence.',
        'Chez J.House of Secretary, nous ne gérons pas simplement des appels et des rendez-vous. Nous accompagnons des personnes dans les moments où elles cherchent un guide, des soins et un accès aux bons services de santé.',
        'À travers nos services de secrétariat, la Ligne du Patient et l\u2019Accès International à la Santé, nous veillons à ce que chaque personne se sente écoutée, comprise et accompagnée avec respect et responsabilité.',
        'Du premier appel téléphonique à la mise en relation avec des spécialistes de santé à Chypre et à l\u2019étranger, notre mission est de transformer l\u2019incertitude en orientation claire, les obstacles en solutions et l\u2019angoisse en sentiment de confiance.',
      ],
      closing: 'Parce que derrière chaque appel ne se cache pas simplement un patient, mais un être humain qui mérite de sentir qu\u2019il n\u2019est pas seul sur le chemin de la santé.',
    },
    nav: {
      home: 'Accueil',
      services: 'Services',
      whyUs: 'Pourquoi Nous',
      testimonials: 'Témoignages',
      faq: 'FAQ',
      contact: 'Contact',
      pricing: 'Tarifs',
    },
    hero: {
      tagline: 'Votre Partenaire de Confiance pour les Services de Secrétariat Professionnel',
      headline: 'Ne Manquez Plus Jamais un Appel Patient',
      title: 'Bienvenue chez',
      subtitle: 'J.House of Secretary',
      description: 'Votre partenaire de confiance pour les services de télésecrétariat professionnel, corporatif et médical, dévoué à améliorer l\'efficacité de votre entreprise.',
      cta: 'Contactez-Nous',
      ctaSecondary: 'Explorer les Services',
      ctaConsultation: 'Réserver une Consultation Gratuite',
      available: 'Disponible 24/7',
    },
    heroFeatures: {
      callAnswering: 'Réponse aux appels 24/7',
      appointmentReminders: 'Rappels de rendez-vous',
      patientFollowUp: 'Suivi des patients',
      telemedicineScheduling: 'Planification télémédecine',
      patientIntake: 'Accueil des patients',
      clinicWorkflow: 'Support flux de travail clinique',
    },
    services: {
      title: 'Services de Télésecrétariat Complets',
      subtitle: 'Optimisez vos opérations avec le soutien de nos télésecrétaires spécialisés.',
      corporate: {
        title: 'Support Télésecrétariat Entreprise',
        description: 'Nos services incluent la gestion des appels, la planification des rendez-vous et le support administratif.',
      },
      medical: {
        title: 'Services Télésecrétariat Médical',
        description: 'Conçus pour les professionnels de santé, nos services assurent un traitement soigneux des appels patients.',
      },
      virtual: {
        title: 'Services d\'Assistant Virtuel',
        description: 'Nos services d\'assistant virtuel fournissent un support flexible et fiable.',
      },
      pricing: {
        title: 'Tarifs',
        description: 'Prix selon le volume de vos appels.',
      },
      gesy: {
        title: 'GESY',
        description: 'Nous collaborons avec: Cliniques médicales privées, Centres de diagnostic, Professionnels de santé, Grandes organisations médicales à Chypre. Nos services soutiennent les équipes médicales dans la gestion de la communication avec les patients.',
      },
    },
    detailedServices: {
      title: 'Nos Services',
      intro: 'Chez J House of Secretary, nous fournissons des services spécialisés de secrétariat médical pour les cabinets privés, cliniques et centres médicaux.',
      phoneCalls: { title: 'Gestion des appels patients', items: ['Réponse aux appels entrants', 'Enregistrement des messages', 'Transfert des appels importants', 'Gestion des demandes', 'Information sur les horaires'] },
      appointments: { title: 'Gestion des rendez-vous', items: ['Prise de nouveaux rendez-vous', 'Modification et annulation', 'Organisation de l\'agenda', 'Éviter les doubles réservations', 'Rappels de rendez-vous'] },
      emailManagement: { title: 'Gestion des emails', items: ['Surveillance des emails', 'Réponse aux demandes basiques', 'Organisation de la correspondance', 'Transfert des messages importants'] },
      patientComm: { title: 'Communication patient', items: ['Information sur les rendez-vous', 'Suivi après visite', 'Réponse aux questions', 'Gestion des demandes'] },
      patientIntake: { title: 'Accueil patient', items: ['Enregistrement des nouveaux patients', 'Collecte d\'informations', 'Organisation des formulaires'] },
      adminSupport: { title: 'Support administratif', items: ['Organisation des dossiers', 'Gestion des documents', 'Préparation des rapports', 'Support quotidien'] },
      telemedicine: { title: 'Support télémédecine', items: ['Organisation des consultations en ligne', 'Envoi des liens', 'Aide technique'] },
      followUp: { title: 'Suivi patient', items: ['Rappels de réexamen', 'Suivi post-traitement', 'Information sur les prochains rendez-vous'] },
      clinicComm: { title: 'Gestion communication clinique', items: ['Coordination médecins-patients', 'Organisation du planning', 'Gestion des demandes'] },
      newPatients: { title: 'Support nouveaux patients', items: ['Information sur les services', 'Guidance première visite', 'Informations examens'] },
      medicalCenters: { title: 'Support centres médicaux', items: ['Coordination des départements', 'Organisation quotidienne', 'Gestion des patients'] },
      remoteSupport: { title: 'Support à distance', items: ['Services de secrétariat à distance', 'Collaboration flexible', 'Réduction des coûts'] },
      gesyReferrals: { title: 'Gestion des références GESY', items: ['Vérification de la validité des références', 'Suivi des approbations et notification des problèmes', 'Rapports pour vous ou votre personnel', 'Gagnez du temps et assurez-vous que toutes les références sont correctes'] }
    },
    gdprSection: {
      title: 'Conformité légale et protection des données',
      description: 'Nos services sont entièrement conformes au RGPD et à la législation GESY, garantissant que toutes les données des patients sont traitées en toute sécurité et confidentialité.',
    },
    whyUs: {
      title: 'Pourquoi Choisir J.House of Secretary ?',
      subtitle: 'Découvrez les avantages uniques de travailler avec nous.',
      expertise: {
        title: 'Expertise Professionnelle',
        description: 'Notre équipe est composée de professionnels expérimentés.',
      },
      personalized: {
        title: 'Approches Personnalisées',
        description: 'Nous sommes fiers de notre capacité à personnaliser nos services.',
      },
      commitment: {
        title: 'Engagement envers l\'Excellence',
        description: 'Notre engagement indéfectible envers l\'excellence et la satisfaction client.',
      },
    },
    facilities: {
      title: 'Nos Installations Modernes',
      subtitle: 'Découvrez notre espace de bureau ultramoderne conçu pour des services de télésecrétariat professionnels.',
    },
    testimonials: {
      title: 'Ce que Disent Nos Clients',
      subtitle: 'Écoutez les entreprises qui nous font confiance.',
      items: [
        { name: 'Dr. Andreas K.', role: 'Directeur de Clinique', text: 'J.House of Secretary a transformé notre gestion des appels patients.' },
        { name: 'Maria S.', role: 'Chef d\'Entreprise', text: 'Le service de télésecrétariat corporatif a été inestimable.' },
        { name: 'Christos P.', role: 'Associé Cabinet Juridique', text: 'Service exceptionnel. Le support multilingue est remarquable.' },
      ],
    },
    faq: {
      title: 'Questions Fréquentes',
      subtitle: 'Trouvez des réponses aux questions courantes.',
      items: [
        { question: 'Quels services proposez-vous ?', answer: 'Nous offrons des services de télésecrétariat complets.' },
        { question: 'Vos services sont-ils disponibles 24/7 ?', answer: 'Oui, notre équipe est disponible 24h/24.' },
        { question: 'Quelles langues supportez-vous ?', answer: 'Grec, anglais, russe, arabe, mandarin, français et espagnol.' },
        { question: 'Comment sont déterminés les tarifs ?', answer: 'Les tarifs sont basés sur le volume d\'appels.' },
        { question: 'Comment démarrer ?', answer: 'Contactez-nous simplement par téléphone ou email.' },
      ],
    },
    contact: {
      title: 'Contactez-Nous',
      subtitle: 'Entrez en contact avec notre équipe pour une consultation personnalisée.',
      form: {
        name: 'Votre Nom',
        email: 'Email',
        phone: 'Téléphone',
        message: 'Votre Message',
        submit: 'Envoyer',
        sending: 'Envoi...',
        success: 'Message envoyé avec succès !',
        error: 'Échec de l\'envoi. Veuillez réessayer.',
      },
      info: {
        phone: 'Téléphone',
        mobile: 'Mobile',
        email: 'Email',
        address: 'Adresse',
        addressLine: 'Afroditis 10, Nicosie 1060, Chypre',
      },
    },
    chat: {
      title: 'Chat en Direct',
      placeholder: 'Tapez votre message...',
      welcome: 'Bonjour ! Comment pouvons-nous vous aider ?',
      offline: 'Nous sommes actuellement hors ligne. Laissez un message.',
    },
    footer: {
      rights: 'Tous Droits Réservés',
      privacy: 'Politique de Confidentialité',
      terms: 'Conditions d\'Utilisation',
    },
    partners: {
      title: 'Nos Partenaires',
      subtitle: 'Approuvé par les principales institutions de santé de Chypre',
    },
    patientLine: {
      back: 'Retour',
      phone: '7000 0223',
      tagline: 'Le Nouveau Service Innovant de J.House of Secretary',
      title: 'La Ligne du Patient',
      slogan: '"Votre santé, une longueur d\'avance."',
      free: 'Gratuit pour tous les patients — sans frais.',
      mainMessage: 'Fini les tracas du patient.',
      subMessage: 'Vous appelez — nous nous en occupons.',
      servicesTitle: 'Ce que nous faisons pour vous',
      services: [
        'Prise de rendez-vous avec la spécialité souhaitée',
        'Accès aux centres de radiologie et d\'hématologie',
        'Recherche de la pharmacie la plus proche',
        'Vérification des références GESY',
        'Rappels de rendez-vous',
        'Suivi après examen'
      ],
      stepsTitle: 'Étapes pour les Patients',
      stepsSubtitle: 'Seulement 4 étapes pour résoudre tout besoin de santé',
      steps: [
        { number: '01', title: 'Appelez la Ligne Patient', description: 'Appelez le 7000 0223 / 801 101 0364 et parlez à notre équipe spécialisée.' },
        { number: '02', title: 'Dites-nous ce dont vous avez besoin', description: 'Rendez-vous avec spécialité, examen, accès à un centre de diagnostic ou pharmacie.' },
        { number: '03', title: 'Nous nous occupons de tout', description: 'Nous prenons rendez-vous, vérifions les références, trouvons le point de service le plus proche.' },
        { number: '04', title: 'Vous recevez la confirmation', description: 'Nous vous informons de la date, l\'heure et le lieu de votre rendez-vous.' }
      ],
      benefitsTitle: 'Fini le Stress',
      benefitsSubtitle: 'Pourquoi choisir la Ligne Patient',
      benefits: [
        { title: 'Un téléphone pour tout', description: 'Appelez seulement le 7000 0223 / 801 101 0364 pour tout besoin de santé.' },
        { title: 'Gain de temps', description: 'Plus d\'attente ni d\'appels interminables.' },
        { title: '100% Gratuit', description: 'Aucun frais pour les patients.' },
        { title: 'Soin personnalisé', description: 'Service personnalisé pour chaque patient.' },
        { title: 'Compatible GESY', description: 'Entièrement aligné avec le Système de Santé Général.' },
        { title: 'Conforme GDPR', description: 'Protection absolue de vos données personnelles.' }
      ],
      ctaTitle: 'Appelez maintenant',
      ctaSubtitle: 'Disponibles chaque jour pour vous servir',
      compatible: 'Compatible avec',
    },
    international: {
      back: 'Retour',
      pageTitle: 'Accès International à la Santé',
      subtitle: 'Coordination pour les patients recherchant des solutions spécialisées à l\'étranger',
      description: 'Nous assurons la coordination professionnelle des patients recherchant des services médicaux avancés, un deuxième avis ou un traitement dans des centres de santé internationaux reconnus. Avec une discrétion absolue et un accompagnement personnalisé à chaque étape.',
      missionQuote: '"Helping healthcare work better — Des solutions de soins spécialisés au-delà des frontières"',
      servicesTitle: 'Services de Coordination Internationale',
      service1Title: 'Coordination de cas médicaux à l\'étranger',
      service1Desc: 'Coordination complète du traitement dans des centres internationaux reconnus',
      service2Title: 'Deuxième avis médical',
      service2Desc: 'Accès à des médecins spécialisés d\'institutions de premier plan',
      service3Title: 'Support dossier médical et communication',
      service3Desc: 'Gestion complète des documents, traductions et correspondances',
      service4Title: 'Organisation de rendez-vous et procédures',
      service4Desc: 'Prise de rendez-vous, préparation d\'examens, logistique',
      service5Title: 'Accompagnement personnalisé des patients',
      service5Desc: 'Support individualisé à chaque étape du processus',
      service6Title: 'Premium Patient Assistance',
      service6Desc: 'Services concierge pour patients VIP avec discrétion absolue',
      whoTitle: 'À Qui S\'adresse Ce Service',
      patients: 'Patients',
      patientsDesc: 'Nous vous aidons à organiser un accès sûr à des centres de santé spécialisés, avec un soutien complet de Chypre jusqu\'à votre arrivée',
      doctorsTitle: 'Médecins et Cliniques',
      doctorsDesc: 'Nous prenons en charge la coordination administrative et internationale des cas complexes',
      hospitalsTitle: 'Hôpitaux et Institutions',
      hospitalsDesc: 'Grâce à des partenariats internationaux avec des organisations de santé reconnues, nous fournissons un accès à des services spécialisés',
      ctaTitle: 'Besoin d\'un Traitement Spécialisé à l\'Étranger ?',
      ctaDesc: 'Accès rapide aux spécialistes via notre bureau de coordination à Chypre',
      ctaConsultation: 'Demander une Consultation',
      ctaUpload: 'Envoyer des Rapports Médicaux',
      ctaCall: 'Parler avec un Coordinateur',
      faqTitle: 'Questions Fréquentes',
      faqSubtitle: 'Accès International à la Santé',
      featuredPartnerLabel: 'Centre Médical Partenaire',
      holisticTagline: 'Soins Médicaux de Classe Mondiale en Israël',
      holisticDescription: 'Classé parmi les meilleurs hôpitaux du monde par Newsweek, le Sheba Medical Center de Tel HaShomer est mondialement reconnu en oncologie, cardiologie, transplantation, fertilité, neurologie et chirurgie avancée. Via Sheba Global et en coordination avec J.House of Secretary, les patients internationaux bénéficient des meilleurs médecins, d\u2019une recherche de pointe et d\u2019une prise en charge entièrement personnalisée et multilingue — de la première demande jusqu\u2019au suivi post-opératoire.',
      holisticTags: ['Oncologie', 'Cardiologie', 'Transplantation', 'Patients internationaux'],
      uploadModal: {
        title: 'Envoyer des Documents Médicaux',
        subtitle: 'Envoyez vos rapports médicaux et fichiers en toute sécurité',
        nameLabel: 'Nom complet',
        phoneLabel: 'Téléphone',
        emailLabel: 'Email',
        messageLabel: 'Message (facultatif)',
        messagePlaceholder: 'Décrivez brièvement votre cas...',
        filesLabel: 'Fichiers / Dossiers',
        filesHint: 'Tous les types de fichiers acceptés • 25 Mo maximum au total',
        selectFiles: 'Sélectionner des Fichiers',
        selectFolder: 'Sélectionner un Dossier',
        clearFiles: 'Effacer',
        gdprConsent: 'Je consens au traitement de mes données conformément au RGPD aux fins de cette demande.',
        submitBtn: 'Envoyer',
        cancelBtn: 'Annuler',
        sending: 'Envoi en cours...',
        successMsg: 'Votre demande a été envoyée avec succès. Nous vous contacterons sous peu.',
        errorMsg: 'L\'envoi a échoué. Veuillez réessayer ou nous contacter directement.',
        sizeError: 'La taille totale des fichiers dépasse la limite de 25 Mo.',
        requiredError: 'Veuillez remplir tous les champs obligatoires.',
        gdprError: 'Veuillez accepter le consentement RGPD pour continuer.',
        rateLimitError: 'Trop de requêtes. Veuillez réessayer dans {minutes} minute(s).',
        filesSelected: 'fichier(s) sélectionné(s)',
      },
      faqItems: [
        { q: 'Qu\'est-ce que le service d\'Accès International à la Santé ?', a: 'Il s\'agit d\'un service de coordination professionnelle pour les patients recherchant un deuxième avis, un traitement spécialisé ou un accès à des centres de santé internationaux reconnus.' },
        { q: 'Fournissez-vous des conseils médicaux ?', a: 'Non. Nous ne fournissons pas de diagnostic ni de traitement médical. Nous assurons la coordination administrative et organisationnelle entre les patients et les prestataires de santé internationaux.' },
        { q: 'Comment commence le processus ?', a: 'Il suffit de nous contacter et de nous envoyer des informations de base ou des examens disponibles, afin d\'évaluer le parcours de coordination le plus approprié.' },
        { q: 'Dois-je voyager à l\'étranger ?', a: 'Pas toujours. Dans de nombreux cas, un deuxième avis ou une évaluation à distance peut être organisé initialement.' },
        { q: 'Puis-je envoyer des examens électroniquement ?', a: 'Oui. Vous pouvez envoyer des examens, rapports et documents médicaux de manière sécurisée par voie électronique.' },
        { q: 'Dans quels pays pouvez-vous aider ?', a: 'Selon le cas et la disponibilité des organisations partenaires, nous offrons un accès à des centres de santé internationaux sélectionnés.' },
        { q: 'À quelle vitesse une demande peut-elle être traitée ?', a: 'Le délai varie selon le cas, mais nous donnons la priorité aux cas urgents et graves avec une coordination immédiate.' },
        { q: 'Proposez-vous des services en grec ?', a: 'Oui. Nous offrons un soutien complet en grec et, si nécessaire, en anglais.' },
        { q: 'Quels types de cas traitez-vous ?', a: 'Nous prenons en charge des cas tels que deuxième avis, cas complexes, traitements spécialisés, oncologie, cardiologie, pédiatrie, neurologie et autres.' },
        { q: 'Prenez-vous en charge les rendez-vous ?', a: 'Oui. Nous pouvons vous aider à planifier les rendez-vous, procédures et étapes associées.' },
        { q: 'Fournissez-vous un soutien pendant le traitement ?', a: 'Oui. Nous restons disponibles pour la coordination administrative et l\'accompagnement à chaque étape.' },
        { q: 'Qu\'en est-il de la confidentialité de mes données personnelles ?', a: 'Toutes les informations sont traitées avec professionnalisme, discrétion et respect de la vie privée.' },
        { q: 'Combien coûte le service ?', a: 'Le coût dépend du type de demande et des exigences de coordination. Une information détaillée est fournie avant le début.' },
        { q: 'Puis-je demander seulement un deuxième avis ?', a: 'Bien sûr. De nombreux patients demandent d\'abord un deuxième avis avant de décider des prochaines étapes.' },
        { q: 'Collaborez-vous avec des médecins et des cliniques ?', a: 'Oui. Nous soutenons également les professionnels de santé, cliniques et organisations nécessitant une coordination internationale des cas.' },
        { q: 'Desservez-vous toute Chypre ?', a: 'Oui. Nous offrons un service pan-chypriote avec possibilité de communication à distance.' },
        { q: 'Puis-je vous contacter via WhatsApp ?', a: 'Oui. La communication directe est disponible par téléphone et WhatsApp.' },
        { q: 'Quel est votre rôle dans le processus ?', a: 'Notre rôle est de simplifier un processus complexe et de servir de point de coordination entre le patient et les acteurs de santé internationaux.' },
        { q: 'Que se passe-t-il en cas d\'urgence ?', a: 'Vous pouvez nous contacter immédiatement pour évaluer le moyen de soutien le plus rapide possible.' },
        { q: 'Comment puis-je commencer aujourd\'hui ?', a: 'Vous pouvez nous contacter par téléphone, WhatsApp ou via le formulaire de contact de notre site web.' },
      ],
      footer: 'Coordination Santé • Accès International • Support Professionnel',
    },
    cookieConsent: {
      title: 'Nous utilisons des cookies',
      message: 'Ce site web utilise des cookies pour vous garantir la meilleure expérience. Nous utilisons uniquement les cookies nécessaires aux fonctionnalités essentielles et à l\'analyse pour améliorer nos services.',
      learnMore: 'En savoir plus',
      acceptAll: 'Tout accepter',
      necessaryOnly: 'Uniquement nécessaires',
      policyTitle: 'Politique des cookies',
      section1Title: 'Que sont les cookies ?',
      section1Body: 'Les cookies sont de petits fichiers texte stockés sur votre appareil lorsque vous visitez un site. Ils aident le site à fonctionner correctement et offrent une meilleure expérience.',
      section2Title: 'Comment nous utilisons les cookies',
      section2Body: 'Nous utilisons des cookies pour les fonctionnalités essentielles (sessions de connexion, préférences linguistiques) et pour comprendre comment les visiteurs interagissent avec notre site.',
      section3Title: 'Types de cookies que nous utilisons',
      cookieType1: 'Cookies essentiels — requis pour le fonctionnement du site',
      cookieType2: 'Cookies fonctionnels — mémorisent vos préférences',
      cookieType3: 'Cookies analytiques — nous aident à comprendre l\'utilisation (anonyme)',
      section4Title: 'Vos droits (RGPD)',
      section4Body: 'Vous pouvez modifier ou retirer votre consentement à tout moment en effaçant les données de votre navigateur. Vous avez également le droit d\'accéder, de corriger ou de supprimer vos données personnelles conformément au RGPD.',
      contactTitle: 'Nous contacter',
    },
  },
  es: {
    sheba: { badge: 'REPRESENTANTES AUTORIZADOS DE SHEBA' },
    splash: {
      back: 'Atrás',
      tagline: 'Coordinación Sanitaria • Acceso Internacional • Soporte Profesional',
      forDoctors: 'Para Médicos Clínicas Hospitales',
      forPatients: 'Para Pacientes',
      internationalAccess: 'Acceso Internacional a la Salud',
      trustedBy: 'CONFÍAN EN NOSOTROS',
      nightPharmacies: 'FARMACIAS NOCTURNAS',
      nightPharmaciesDesc: 'Encuentre la farmacia nocturna más cercana',
      nightPharmaciesFooter: 'Los datos se actualizan diariamente.',
      disclaimer1: 'La Línea del Paciente proporciona servicios de facilitación para encontrar médicos disponibles y programar citas.',
      disclaimer2: 'El servicio puede sugerir médicos asociados disponibles según disponibilidad y necesidades del paciente.',
      disclaimer3: 'La elección final del médico la realiza exclusivamente el paciente.',
      disclaimer4: 'Los médicos asociados participan a través de una suscripción anual para la participación en la red.',
      disclaimer5: 'El servicio no proporciona asesoramiento médico ni sustituye la relación médico-paciente.',
    },
    philosophy: {
      eyebrow: 'NUESTRA FILOSOFÍA',
      etymology: 'La palabra griega «ασθενής» (paciente) proviene del prefijo privativo «α-» y «σθένος» (la fuerza) — designando a quien necesita apoyo, cuidado y recuperar su fuerza.',
      paragraphs: [
        'Creemos que cada comunicación con un profesional de la salud es una oportunidad para devolver un poco de esa fuerza. Por eso prestamos especial atención a la primera llamada — donde la cortesía, la comprensión y la profesionalidad pueden marcar toda la diferencia.',
        'En J.House of Secretary no nos limitamos a gestionar llamadas y citas. Acompañamos a personas en momentos en los que buscan orientación, cuidado y acceso a los servicios de salud adecuados.',
        'A través de nuestros servicios de apoyo secretarial, la Línea del Paciente y el Acceso Internacional a la Salud, nos aseguramos de que cada persona se sienta escuchada, comprendida y guiada con respeto y responsabilidad.',
        'Desde la primera llamada telefónica hasta la conexión con profesionales de la salud especializados en Chipre y en el extranjero, nuestra misión es transformar la incertidumbre en orientación clara, los obstáculos en soluciones y la ansiedad en una sensación de confianza.',
      ],
      closing: 'Porque detrás de cada llamada no hay simplemente un paciente, sino un ser humano que merece sentir que no está solo en su camino hacia la salud.',
    },
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      whyUs: 'Por Qué Nosotros',
      testimonials: 'Testimonios',
      faq: 'Preguntas',
      contact: 'Contacto',
      pricing: 'Precios',
    },
    hero: {
      tagline: 'Su Socio de Confianza en Servicios de Secretaría Profesional',
      headline: 'Nunca Más Pierda una Llamada de Paciente',
      title: 'Bienvenido a',
      subtitle: 'J.House of Secretary',
      description: 'Su socio de confianza en servicios de telesecretaría profesional, corporativa y médica, dedicados a mejorar la eficiencia de su negocio.',
      cta: 'Contáctenos',
      ctaSecondary: 'Explorar Servicios',
      ctaConsultation: 'Reserve una Consulta Gratuita',
      available: 'Disponible 24/7',
    },
    heroFeatures: {
      callAnswering: 'Respuesta de llamadas 24/7',
      appointmentReminders: 'Recordatorios de citas',
      patientFollowUp: 'Seguimiento de pacientes',
      telemedicineScheduling: 'Programación de telemedicina',
      patientIntake: 'Recepción de pacientes',
      clinicWorkflow: 'Soporte de flujo de trabajo clínico',
    },
    services: {
      title: 'Servicios Integrales de Telesecretaría',
      subtitle: 'Optimice sus operaciones con el apoyo de nuestros telesecretarios especializados.',
      corporate: {
        title: 'Soporte de Telesecretaría Corporativa',
        description: 'Nuestros servicios incluyen gestión de llamadas, programación de citas y soporte administrativo.',
      },
      medical: {
        title: 'Servicios de Telesecretaría Médica',
        description: 'Diseñados para profesionales de la salud, aseguramos que las llamadas de pacientes se manejen con cuidado.',
      },
      virtual: {
        title: 'Servicios de Asistente Virtual',
        description: 'Nuestros servicios de asistente virtual proporcionan soporte flexible y confiable.',
      },
      pricing: {
        title: 'Precios',
        description: 'Precios según el volumen de sus llamadas.',
      },
      gesy: {
        title: 'GESY',
        description: 'Colaboramos con: Clínicas médicas privadas, Centros de diagnóstico, Profesionales de la salud, Grandes organizaciones médicas en Chipre. Nuestros servicios apoyan a los equipos médicos en la gestión de la comunicación con los pacientes.',
      },
    },
    detailedServices: {
      title: 'Nuestros Servicios',
      intro: 'En J House of Secretary, proporcionamos servicios especializados de secretaría médica para consultorios privados, clínicas y centros médicos.',
      phoneCalls: { title: 'Gestión de llamadas de pacientes', items: ['Respuesta a llamadas entrantes', 'Registro de mensajes', 'Transferencia de llamadas importantes', 'Gestión de consultas', 'Información de horarios'] },
      appointments: { title: 'Gestión de citas', items: ['Reserva de nuevas citas', 'Cambio y cancelación', 'Organización del calendario', 'Evitar reservas dobles', 'Recordatorios de citas'] },
      emailManagement: { title: 'Gestión de email', items: ['Monitoreo de correos', 'Respuesta a solicitudes básicas', 'Organización de correspondencia', 'Reenvío de mensajes importantes'] },
      patientComm: { title: 'Comunicación con pacientes', items: ['Información sobre citas', 'Seguimiento post-visita', 'Respuesta a preguntas', 'Gestión de solicitudes'] },
      patientIntake: { title: 'Recepción de pacientes', items: ['Registro de nuevos pacientes', 'Recopilación de información', 'Organización de formularios'] },
      adminSupport: { title: 'Soporte administrativo', items: ['Organización de expedientes', 'Gestión de documentos', 'Preparación de informes', 'Soporte diario'] },
      telemedicine: { title: 'Soporte de telemedicina', items: ['Organización de consultas online', 'Envío de enlaces', 'Ayuda técnica'] },
      followUp: { title: 'Seguimiento de pacientes', items: ['Recordatorios de revisión', 'Seguimiento post-tratamiento', 'Información sobre próximas citas'] },
      clinicComm: { title: 'Gestión de comunicación', items: ['Coordinación médicos-pacientes', 'Organización del horario', 'Gestión de solicitudes'] },
      newPatients: { title: 'Soporte nuevos pacientes', items: ['Información sobre servicios', 'Guía para primera visita', 'Información de exámenes'] },
      medicalCenters: { title: 'Soporte centros médicos', items: ['Coordinación de departamentos', 'Organización diaria', 'Gestión de pacientes'] },
      remoteSupport: { title: 'Soporte remoto', items: ['Servicios de secretaría remota', 'Colaboración flexible', 'Reducción de costos'] },
      gesyReferrals: { title: 'Gestión de referencias GESY', items: ['Verificación de validez de referencias', 'Seguimiento de aprobaciones y notificación de problemas', 'Informes para usted o su personal', 'Ahorre tiempo y asegúrese de que todas las referencias sean correctas'] }
    },
    gdprSection: {
      title: 'Cumplimiento legal y protección de datos',
      description: 'Nuestros servicios cumplen totalmente con GDPR y la legislación GESY, garantizando que todos los datos de pacientes se manejen de forma segura y confidencial.',
    },
    whyUs: {
      title: '¿Por Qué Elegir J.House of Secretary?',
      subtitle: 'Descubra las ventajas únicas de trabajar con nosotros.',
      expertise: {
        title: 'Experiencia Profesional',
        description: 'Nuestro equipo está formado por profesionales experimentados.',
      },
      personalized: {
        title: 'Enfoques Personalizados',
        description: 'Nos enorgullecemos de nuestra capacidad para personalizar servicios.',
      },
      commitment: {
        title: 'Compromiso con la Excelencia',
        description: 'Nuestro compromiso inquebrantable con la excelencia y satisfacción del cliente.',
      },
    },
    facilities: {
      title: 'Nuestras Instalaciones Modernas',
      subtitle: 'Descubra nuestro moderno espacio de oficina diseñado para servicios profesionales de telesecretaría.',
    },
    testimonials: {
      title: 'Lo Que Dicen Nuestros Clientes',
      subtitle: 'Escuche a las empresas que confían en nosotros.',
      items: [
        { name: 'Dr. Andreas K.', role: 'Director de Clínica', text: 'J.House of Secretary ha transformado cómo manejamos las llamadas de pacientes.' },
        { name: 'María S.', role: 'Propietaria de Negocio', text: 'El servicio de telesecretaría corporativa ha sido invaluable.' },
        { name: 'Christos P.', role: 'Socio Bufete Legal', text: 'Servicio excepcional. El soporte multilingüe es extraordinario.' },
      ],
    },
    faq: {
      title: 'Preguntas Frecuentes',
      subtitle: 'Encuentre respuestas a preguntas comunes.',
      items: [
        { question: '¿Qué servicios ofrecen?', answer: 'Ofrecemos servicios integrales de telesecretaría.' },
        { question: '¿Sus servicios están disponibles 24/7?', answer: 'Sí, nuestro equipo está disponible las 24 horas.' },
        { question: '¿Qué idiomas soportan?', answer: 'Griego, inglés, ruso, árabe, mandarín, francés y español.' },
        { question: '¿Cómo se determinan los precios?', answer: 'Los precios se basan en el volumen de llamadas.' },
        { question: '¿Cómo empiezo?', answer: 'Simplemente contáctenos por teléfono o email.' },
      ],
    },
    contact: {
      title: 'Contáctenos',
      subtitle: 'Póngase en contacto con nuestro equipo para una consulta personalizada.',
      form: {
        name: 'Su Nombre',
        email: 'Correo Electrónico',
        phone: 'Teléfono',
        message: 'Su Mensaje',
        submit: 'Enviar Mensaje',
        sending: 'Enviando...',
        success: '¡Mensaje enviado con éxito!',
        error: 'Error al enviar. Por favor, intente de nuevo.',
      },
      info: {
        phone: 'Teléfono',
        mobile: 'Móvil',
        email: 'Correo',
        address: 'Dirección',
        addressLine: 'Afroditis 10, Nicosia 1060, Chipre',
      },
    },
    chat: {
      title: 'Chat en Vivo',
      placeholder: 'Escriba su mensaje...',
      welcome: '¡Hola! ¿Cómo podemos ayudarle hoy?',
      offline: 'Actualmente estamos desconectados. Deje un mensaje.',
    },
    footer: {
      rights: 'Todos los Derechos Reservados',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
    },
    partners: {
      title: 'Nuestros Socios',
      subtitle: 'Confiado por las principales instituciones de salud de Chipre',
    },
    patientLine: {
      back: 'Atrás',
      phone: '7000 0223',
      tagline: 'El Nuevo Servicio Innovador de J.House of Secretary',
      title: 'La Línea del Paciente',
      slogan: '"Su salud, un paso adelante."',
      free: 'Gratis para todos los pacientes — sin cargo.',
      mainMessage: 'Fin del sufrimiento del paciente.',
      subMessage: 'Usted llama — nosotros lo manejamos.',
      servicesTitle: 'Lo que hacemos por usted',
      services: [
        'Reservar citas con la especialidad que necesita',
        'Acceso a centros de radiología y hematología',
        'Encontrar la farmacia más cercana',
        'Verificación de referencias GESY',
        'Recordatorios de citas',
        'Seguimiento después del examen'
      ],
      stepsTitle: 'Pasos para Pacientes',
      stepsSubtitle: 'Solo 4 pasos para resolver cualquier necesidad de salud',
      steps: [
        { number: '01', title: 'Llame a la Línea del Paciente', description: 'Llame al 7000 0223 / 801 101 0364 y hable con nuestro equipo especializado.' },
        { number: '02', title: 'Díganos lo que necesita', description: 'Cita con especialidad, examen, acceso a centro de diagnóstico o farmacia.' },
        { number: '03', title: 'Nosotros nos encargamos de todo', description: 'Reservamos citas, verificamos referencias, encontramos el punto de servicio más cercano.' },
        { number: '04', title: 'Recibe confirmación', description: 'Le informamos de la fecha, hora y lugar de su cita.' }
      ],
      benefitsTitle: 'Fin del Estrés',
      benefitsSubtitle: 'Por qué elegir la Línea del Paciente',
      benefits: [
        { title: 'Un teléfono para todo', description: 'Llame solo al 7000 0223 / 801 101 0364 para cualquier necesidad de salud.' },
        { title: 'Ahorro de tiempo', description: 'No más esperas ni llamadas interminables.' },
        { title: '100% Gratis', description: 'Sin cargo para los pacientes.' },
        { title: 'Cuidado personal', description: 'Servicio personalizado para cada paciente.' },
        { title: 'Compatible con GESY', description: 'Totalmente alineado con el Sistema General de Salud.' },
        { title: 'Cumple con GDPR', description: 'Protección absoluta de sus datos personales.' }
      ],
      ctaTitle: 'Llame ahora',
      ctaSubtitle: 'Disponibles todos los días para servirle',
      compatible: 'Compatible con',
    },
    international: {
      back: 'Atrás',
      pageTitle: 'Acceso Internacional a la Salud',
      subtitle: 'Coordinación para pacientes que buscan soluciones especializadas en el extranjero',
      description: 'Nos encargamos de la coordinación profesional de pacientes que buscan servicios médicos avanzados, segunda opinión o tratamiento en centros de salud internacionales reconocidos. Con absoluta discreción y apoyo personalizado en cada etapa.',
      missionQuote: '"Helping healthcare work better — Soluciones de atención especializada más allá de las fronteras"',
      servicesTitle: 'Servicios de Coordinación Internacional',
      service1Title: 'Coordinación de casos médicos en el extranjero',
      service1Desc: 'Coordinación completa del tratamiento en centros internacionales reconocidos',
      service2Title: 'Segunda opinión médica',
      service2Desc: 'Acceso a médicos especializados de instituciones líderes',
      service3Title: 'Soporte de expediente médico y comunicación',
      service3Desc: 'Gestión completa de documentos, traducciones y correspondencia',
      service4Title: 'Organización de citas y procedimientos',
      service4Desc: 'Reserva de citas, preparación de exámenes, logística',
      service5Title: 'Orientación personalizada de pacientes',
      service5Desc: 'Apoyo individualizado en cada etapa del proceso',
      service6Title: 'Premium Patient Assistance',
      service6Desc: 'Servicios concierge para pacientes VIP con absoluta discreción',
      whoTitle: '¿A Quién Va Dirigido?',
      patients: 'Pacientes',
      patientsDesc: 'Le ayudamos a organizar un acceso seguro a centros de salud especializados, con soporte completo desde Chipre hasta su llegada',
      doctorsTitle: 'Médicos y Clínicas',
      doctorsDesc: 'Nos encargamos de la coordinación administrativa e internacional de casos complejos',
      hospitalsTitle: 'Hospitales e Instituciones',
      hospitalsDesc: 'A través de asociaciones internacionales con organizaciones de salud reconocidas, proporcionamos acceso a servicios especializados',
      ctaTitle: '¿Necesita Tratamiento Especializado en el Extranjero?',
      ctaDesc: 'Acceso rápido a especialistas a través de nuestra oficina de coordinación en Chipre',
      ctaConsultation: 'Solicitar Consulta',
      ctaUpload: 'Enviar Informes Médicos',
      ctaCall: 'Hablar con Coordinador',
      faqTitle: 'Preguntas Frecuentes',
      faqSubtitle: 'Acceso Internacional a la Salud',
      featuredPartnerLabel: 'Centro Médico Colaborador',
      holisticTagline: 'Atención Médica de Clase Mundial en Israel',
      holisticDescription: 'El Sheba Medical Center en Tel HaShomer figura de forma constante entre los mejores hospitales del mundo según Newsweek y goza de reconocimiento internacional en oncología, cardiología, trasplantes, fertilidad, neurología y cirugía avanzada. A través de Sheba Global y en coordinación con J.House of Secretary, los pacientes internacionales acceden a médicos de primer nivel, investigación de vanguardia y una gestión de caso totalmente personalizada y multilingüe — desde la primera consulta hasta el seguimiento postoperatorio.',
      holisticTags: ['Oncología', 'Cardiología', 'Trasplantes', 'Pacientes internacionales'],
      uploadModal: {
        title: 'Subir Documentos Médicos',
        subtitle: 'Envíe sus informes médicos y archivos de forma segura',
        nameLabel: 'Nombre Completo',
        phoneLabel: 'Teléfono',
        emailLabel: 'Email',
        messageLabel: 'Mensaje (opcional)',
        messagePlaceholder: 'Describa brevemente su caso...',
        filesLabel: 'Archivos / Carpetas',
        filesHint: 'Se aceptan todos los tipos de archivos • Máximo 25MB en total',
        selectFiles: 'Seleccionar Archivos',
        selectFolder: 'Seleccionar Carpeta',
        clearFiles: 'Limpiar',
        gdprConsent: 'Doy mi consentimiento para el tratamiento de mis datos conforme al RGPD para los fines de esta solicitud.',
        submitBtn: 'Enviar',
        cancelBtn: 'Cancelar',
        sending: 'Enviando...',
        successMsg: 'Su solicitud ha sido enviada con éxito. Nos pondremos en contacto pronto.',
        errorMsg: 'Error al enviar. Inténtelo de nuevo o contáctenos directamente.',
        sizeError: 'El tamaño total de los archivos excede el límite de 25MB.',
        requiredError: 'Por favor, complete todos los campos obligatorios.',
        gdprError: 'Por favor, acepte el consentimiento RGPD para continuar.',
        rateLimitError: 'Demasiadas solicitudes. Inténtelo de nuevo en {minutes} minuto(s).',
        filesSelected: 'archivo(s) seleccionado(s)',
      },
      faqItems: [
        { q: '¿Qué es el servicio de Acceso Internacional a la Salud?', a: 'Es un servicio de coordinación profesional para pacientes que buscan una segunda opinión, tratamiento especializado o acceso a centros de salud internacionales reconocidos.' },
        { q: '¿Proporcionan consejos médicos?', a: 'No. No proporcionamos diagnóstico ni tratamiento médico. Nos encargamos de la coordinación administrativa y organizativa entre pacientes y proveedores de salud internacionales.' },
        { q: '¿Cómo comienza el proceso?', a: 'Simplemente contáctenos y envíenos información básica o exámenes disponibles, para evaluar la ruta de coordinación más adecuada.' },
        { q: '¿Necesito viajar al extranjero?', a: 'No siempre. En muchos casos, inicialmente se puede organizar una segunda opinión o evaluación a distancia.' },
        { q: '¿Puedo enviar exámenes electrónicamente?', a: 'Sí. Puede enviar exámenes, informes y documentos médicos de forma segura por vía electrónica.' },
        { q: '¿En qué países pueden ayudar?', a: 'Dependiendo del caso y la disponibilidad de organizaciones asociadas, proporcionamos acceso a centros de salud internacionales seleccionados.' },
        { q: '¿Qué tan rápido puede avanzar una solicitud?', a: 'El tiempo varía según el caso, pero priorizamos casos urgentes y graves con coordinación inmediata.' },
        { q: '¿Ofrecen servicios en griego?', a: 'Sí. Ofrecemos soporte completo en griego y, cuando es necesario, en inglés.' },
        { q: '¿Qué tipos de casos atienden?', a: 'Atendemos casos como segunda opinión, casos complejos, tratamientos especializados, oncología, cardiología, pediatría, neurología y otros.' },
        { q: '¿Se encargan también de las citas?', a: 'Sí. Podemos ayudar a programar citas, procedimientos y pasos relacionados.' },
        { q: '¿Proporcionan apoyo durante el tratamiento?', a: 'Sí. Permanecemos disponibles para la coordinación administrativa y orientación en cada etapa.' },
        { q: '¿Qué pasa con la confidencialidad de mis datos personales?', a: 'Toda la información se maneja con profesionalismo, discreción y respeto a la privacidad.' },
        { q: '¿Cuánto cuesta el servicio?', a: 'El costo depende del tipo de solicitud y los requisitos de coordinación. Se proporciona información detallada antes del inicio.' },
        { q: '¿Puedo pedir solo una segunda opinión?', a: 'Por supuesto. Muchos pacientes solicitan inicialmente una segunda opinión antes de decidir los próximos pasos.' },
        { q: '¿Colaboran con médicos y clínicas?', a: 'Sí. También apoyamos a profesionales de salud, clínicas y organizaciones que necesitan coordinación internacional de casos.' },
        { q: '¿Atienden a toda Chipre?', a: 'Sí. Ofrecemos servicio pan-chipriota con posibilidad de comunicación remota.' },
        { q: '¿Puedo comunicarme por WhatsApp?', a: 'Sí. Está disponible la comunicación directa por teléfono y WhatsApp.' },
        { q: '¿Cuál es su rol en el proceso?', a: 'Nuestro rol es simplificar un proceso complejo y servir como punto de coordinación entre el paciente y las entidades de salud internacionales.' },
        { q: '¿Qué pasa en casos de emergencia?', a: 'Puede contactarnos inmediatamente para evaluar la forma de apoyo más rápida posible.' },
        { q: '¿Cómo puedo comenzar hoy?', a: 'Puede contactarnos por teléfono, WhatsApp o desde el formulario de contacto de nuestro sitio web.' },
      ],
      footer: 'Coordinación Sanitaria • Acceso Internacional • Soporte Profesional',
    },
    cookieConsent: {
      title: 'Utilizamos cookies',
      message: 'Este sitio web utiliza cookies para garantizar la mejor experiencia. Utilizamos solo las cookies necesarias para la funcionalidad esencial y análisis para mejorar nuestros servicios.',
      learnMore: 'Más información',
      acceptAll: 'Aceptar todas',
      necessaryOnly: 'Solo necesarias',
      policyTitle: 'Política de cookies',
      section1Title: '¿Qué son las cookies?',
      section1Body: 'Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita un sitio web. Ayudan a que el sitio funcione correctamente y proporcionan una mejor experiencia.',
      section2Title: 'Cómo usamos las cookies',
      section2Body: 'Utilizamos cookies para la funcionalidad básica del sitio (sesiones de inicio, preferencias de idioma) y para comprender cómo interactúan los visitantes con nuestro sitio.',
      section3Title: 'Tipos de cookies que usamos',
      cookieType1: 'Cookies esenciales — necesarias para el funcionamiento del sitio',
      cookieType2: 'Cookies funcionales — recuerdan sus preferencias',
      cookieType3: 'Cookies analíticas — nos ayudan a entender el uso (anónimo)',
      section4Title: 'Sus derechos (RGPD)',
      section4Body: 'Puede cambiar o retirar su consentimiento en cualquier momento eliminando los datos del navegador. También tiene derecho a acceder, corregir o eliminar sus datos personales conforme al RGPD de la UE.',
      contactTitle: 'Contáctenos',
    },
  },
  he: {
    sheba: { badge: 'נציגים מוסמכים של SHEBA' },
    splash: {
      back: 'חזרה',
      tagline: 'תיאום בריאות • גישה בינלאומית • תמיכה מקצועית',
      forDoctors: 'לרופאים מרפאות בתי חולים',
      forPatients: 'למטופלים',
      internationalAccess: 'גישה בינלאומית לבריאות',
      trustedBy: 'סומכים עלינו',
      nightPharmacies: 'בתי מרקחת לילה',
      nightPharmaciesDesc: 'מצא את בית המרקחת הלילי הקרוב אליך',
      nightPharmaciesFooter: 'הנתונים מתעדכנים מדי יום.',
      disclaimer1: 'קו המטופל מספק שירותי הקלה ושירות למטופלים למציאת רופאים זמינים וקביעת תורים.',
      disclaimer2: 'השירות עשוי להציע רופאים שותפים זמינים בהתאם לזמינות ולצרכי המטופל, מבלי להשפיע על החלטות רפואיות.',
      disclaimer3: 'הבחירה הסופית של הרופא נעשית באופן בלעדי על ידי המטופל.',
      disclaimer4: 'הרופאים השותפים משתתפים באמצעות מנוי שנתי להשתתפות ברשת וניהול בקשות, שאינו קשור למספר מקרים או הפניות.',
      disclaimer5: 'השירות אינו מספק ייעוץ רפואי ואינו מחליף את הקשר רופא-מטופל.',
    },
    philosophy: {
      eyebrow: 'הפילוסופיה שלנו',
      etymology: 'המילה היוונית «ασθενής» (חולה) נגזרת מהקידומת השוללת «α-» ומהמילה «σθένος» (כוח) — מתארת את מי שזקוק לתמיכה, טיפול ושיקום הכוח.',
      paragraphs: [
        'אנו מאמינים שכל תקשורת עם איש מקצוע בתחום הבריאות היא הזדמנות להשיב לאדם מעט מאותו כוח. לכן אנו מקדישים תשומת לב מיוחדת לשיחה הראשונה — שבה אדיבות, הבנה ומקצועיות עושים את כל ההבדל.',
        'ב-J.House of Secretary איננו מנהלים רק שיחות ופגישות. אנו מלווים אנשים ברגעים שבהם הם מחפשים הכוונה, טיפול וגישה לשירותי הבריאות המתאימים.',
        'באמצעות שירותי התמיכה המזכירותית שלנו, קו המטופל וגישת הבריאות הבינלאומית, אנו מבטיחים שכל אדם ירגיש שהוא נשמע, מובן ומלווה בכבוד ובאחריות.',
        'מהשיחה הטלפונית הראשונה ועד החיבור עם אנשי מקצוע מומחים בקפריסין ובחו"ל, שליחותנו היא להפוך אי-ודאות להכוונה ברורה, מכשולים לפתרונות, וחרדה לתחושת ביטחון.',
      ],
      closing: 'משום שמאחורי כל שיחה לא עומד רק מטופל, אלא בן אדם שראוי שירגיש שאינו לבד בדרכו אל הבריאות.',
    },
    nav: {
      home: 'בית',
      services: 'שירותים',
      whyUs: 'למה אנחנו',
      testimonials: 'המלצות',
      faq: 'שאלות נפוצות',
      contact: 'צור קשר',
      pricing: 'מחירים',
    },
    hero: {
      tagline: 'השותף המהימן שלך בשירותי מזכירות מקצועיים',
      headline: 'לעולם אל תפספס שיחת מטופל',
      title: 'ברוכים הבאים ל',
      subtitle: 'J.House of Secretary',
      description: 'השותף המהימן שלך בשירותי מזכירות טלפונית מקצועיים, תאגידיים ורפואיים, המוקדשים לשיפור היעילות העסקית שלך.',
      cta: 'צור קשר',
      ctaSecondary: 'גלה שירותים',
      ctaConsultation: 'הזמן ייעוץ חינם',
      available: 'זמין 24/7',
    },
    heroFeatures: {
      callAnswering: 'מענה שיחות 24/7',
      appointmentReminders: 'תזכורות תורים',
      patientFollowUp: 'מעקב מטופלים',
      telemedicineScheduling: 'תיאום טלרפואה',
      patientIntake: 'קליטת מטופלים',
      clinicWorkflow: 'תמיכת תהליכי מרפאה',
    },
    valueProposition: {
      headline: 'ארגן את המרפאה שלך בקלות – תמיכה מזכירותית מקצועית',
      subheadline: 'חסוך זמן, הפחת שגיאות וקצץ עלויות.',
      ctaDemo: 'הזמן הדגמה חינם',
      benefits: {
        callManagement: 'ניהול שיחות ותורים – תמיכה מקצועית ללא עיכובים.',
        gesyReferrals: 'אימות הפניות GESY – תהליך מדויק ומאובטח.',
        costSaving: 'חיסכון בעלויות – תמיכה מזכירותית מלאה בעלות נמוכה משמעותית.',
        compliance: 'ציות ואבטחת מידע – בהתאם ל-GDPR.',
        packages: 'חבילות שירות מותאמות אישית.',
      },
    },
    services: {
      title: 'שירותי מזכירות טלפונית מקיפים',
      subtitle: 'ייעל את הפעילות שלך בתמיכת המזכירות הטלפוניות המתמחות שלנו.',
      corporate: {
        title: 'תמיכה מזכירותית תאגידית',
        description: 'שירותי המזכירות התאגידית שלנו כוללים ניהול שיחות, תיאום תורים ותמיכה מנהלית.',
      },
      medical: {
        title: 'שירותי מזכירות רפואית',
        description: 'מעוצב עבור אנשי מקצוע בתחום הבריאות, מבטיח טיפול מקצועי בשיחות מטופלים.',
      },
      virtual: {
        title: 'שירותי עוזר וירטואלי',
        description: 'שירותי עוזר וירטואלי מספקים תמיכה גמישה ואמינה.',
      },
      pricing: {
        title: 'מחירים',
        description: 'מחירים לפי נפח השיחות שלך.',
      },
      gesy: {
        title: 'GESY',
        description: 'אנו משתפים פעולה עם מרפאות פרטיות, מרכזי אבחון ואנשי מקצוע רפואיים בקפריסין.',
      },
    },
    detailedServices: {
      title: 'השירותים שלנו',
      intro: 'אנו מספקים שירותי מזכירות רפואית מתמחים למרפאות פרטיות ומרכזים רפואיים.',
      phoneCalls: { title: 'ניהול שיחות מטופלים', items: ['מענה שיחות נכנסות', 'רישום הודעות', 'העברת שיחות חשובות', 'ניהול פניות', 'מידע על שעות פעילות'] },
      appointments: { title: 'ניהול תורים', items: ['קביעת תורים חדשים', 'שינוי וביטול תורים', 'ארגון יומן הרופא', 'מניעת כפילויות', 'תזכורות תורים'] },
      emailManagement: { title: 'ניהול דוא"ל', items: ['מעקב דוא"ל נכנס', 'מענה לפניות בסיסיות', 'ארגון התכתבות', 'העברת הודעות חשובות'] },
      patientComm: { title: 'תקשורת מטופלים', items: ['עדכון מטופלים על תורים', 'מעקב אחרי ביקורים', 'מענה לשאלות', 'ניהול פניות חדשות'] },
      patientIntake: { title: 'קליטת מטופלים', items: ['רישום פרטי מטופלים חדשים', 'איסוף מידע לפני תורים', 'ארגון טפסים'] },
      adminSupport: { title: 'תמיכה מנהלית', items: ['ארגון תיקי מטופלים', 'ניהול מסמכים', 'הכנת דוחות', 'תמיכת משימות יומיות'] },
      telemedicine: { title: 'תמיכת טלרפואה', items: ['ארגון ייעוץ מקוון', 'שליחת קישורים', 'עזרה טכנית למטופלים'] },
      followUp: { title: 'מעקב מטופלים', items: ['תזכורות בדיקות חוזרות', 'מעקב אחרי טיפול', 'מידע על תורים הבאים'] },
      clinicComm: { title: 'ניהול תקשורת מרפאה', items: ['תיאום בין רופאים למטופלים', 'ארגון לוח זמנים', 'ניהול בקשות שיתוף פעולה'] },
      newPatients: { title: 'תמיכת מטופלים חדשים', items: ['מידע על שירותי המרפאה', 'הדרכה לביקור ראשון', 'מידע בסיסי על בדיקות'] },
      medicalCenters: { title: 'תמיכת מרכזים רפואיים', items: ['תיאום בין מחלקות', 'ארגון פעילות יומית', 'ניהול מטופלים'] },
      remoteSupport: { title: 'תמיכה מזכירותית מרחוק', items: ['שירותי מזכירות מרחוק', 'שיתוף פעולה גמיש', 'הפחתת עלויות כוח אדם'] },
      gesyReferrals: { title: 'ניהול הפניות GESY', items: ['בדיקת תוקף הפניות', 'מעקב אישורים', 'דוחות', 'חיסכון בזמן'] },
    },
    gdprSection: {
      title: 'ציות לחקיקה והגנת מידע',
      description: 'השירותים שלנו עומדים בדרישות GDPR וחקיקת GESY.',
    },
    whyUs: {
      title: 'למה לבחור ב-J.House of Secretary?',
      subtitle: 'גלה את היתרונות הייחודיים של העבודה איתנו.',
      expertise: { title: 'מומחיות מקצועית', description: 'צוות מנוסה עם ניסיון רב בשירותי מזכירות טלפונית.' },
      personalized: { title: 'גישות מותאמות אישית', description: 'אנו מתאימים את השירותים לצרכים הספציפיים שלך.' },
      commitment: { title: 'מחויבות למצוינות', description: 'מחויבות בלתי מתפשרת למצוינות ושביעות רצון הלקוחות.' },
    },
    facilities: { title: 'המתקנים המודרניים שלנו', subtitle: 'גלה את חלל המשרדים המתקדם שלנו.' },
    testimonials: {
      title: 'מה הלקוחות שלנו אומרים',
      subtitle: 'שמע מעסקים שסומכים עלינו.',
      items: [
        { name: 'ד"ר אנדריאס ק.', role: 'מנהל מרפאה', text: 'J.House of Secretary שינו את הדרך שבה אנו מטפלים בשיחות מטופלים. מקצועי, יעיל ותמיד זמין.' },
        { name: 'מריה ס.', role: 'בעלת עסק', text: 'שירות המזכירות התאגידית היה בלתי יסולא בפז.' },
        { name: 'כריסטוס פ.', role: 'שותף במשרד עורכי דין', text: 'שירות מצוין. התמיכה הרב-לשונית יוצאת דופן.' },
      ],
    },
    faq: {
      title: 'שאלות נפוצות',
      subtitle: 'מצא תשובות לשאלות נפוצות.',
      items: [
        { question: 'אילו שירותים אתם מציעים?', answer: 'אנו מציעים שירותי מזכירות טלפונית מקיפים.' },
        { question: 'השירותים זמינים 24/7?', answer: 'כן, הצוות שלנו זמין 24 שעות ביממה.' },
        { question: 'באילו שפות אתם תומכים?', answer: 'אנו מציעים תמיכה רב-לשונית ביוונית, אנגלית, רוסית, ערבית, סינית, צרפתית, ספרדית ועברית.' },
        { question: 'כיצד נקבעים המחירים?', answer: 'המחירים מבוססים על נפח השיחות והדרישות הספציפיות שלך.' },
        { question: 'איך מתחילים?', answer: 'פשוט צור קשר בטלפון או בדוא"ל והצוות שלנו ידריך אותך.' },
      ],
    },
    contact: {
      title: 'צור קשר',
      subtitle: 'פנה לצוות שלנו לייעוץ מותאם אישית.',
      form: { name: 'השם שלך', email: 'כתובת דוא"ל', phone: 'מספר טלפון', message: 'ההודעה שלך', submit: 'שלח הודעה', sending: 'שולח...', success: 'ההודעה נשלחה בהצלחה!', error: 'שליחה נכשלה. נסה שוב.' },
      info: { phone: 'טלפון', mobile: 'נייד', email: 'דוא"ל', address: 'כתובת', addressLine: 'אפרודיטיס 10, ניקוסיה 1060, קפריסין' },
    },
    chat: { title: 'צ\'אט חי', placeholder: 'הקלד את ההודעה שלך...', welcome: 'שלום! איך נוכל לעזור לך היום?', offline: 'אנחנו כרגע לא מקוונים. אנא השאר הודעה.' },
    footer: { rights: 'כל הזכויות שמורות', privacy: 'מדיניות פרטיות', terms: 'תנאי שירות' },
    partners: { title: 'השותפים שלנו', subtitle: 'נבחרו על ידי מוסדות בריאות מובילים בקפריסין' },
    patientLine: {
      back: 'חזרה',
      phone: '7000 0223',
      tagline: 'השירות החדשני החדש מ-J.House of Secretary',
      title: 'קו המטופל',
      slogan: '"הבריאות שלך, צעד אחד קדימה."',
      free: 'חינם לכל המטופלים — ללא תשלום.',
      mainMessage: 'סוף לטרדות המטופל.',
      subMessage: 'אתה מתקשר — אנחנו מטפלים.',
      servicesTitle: 'מה אנחנו מטפלים עבורך',
      services: ['קביעת תורים עם ההתמחות הנדרשת', 'גישה למרכזי רדיולוגיה והמטולוגיה', 'מציאת בית המרקחת הקרוב', 'תזכורות תורים', 'מעקב אחרי בדיקה'],
      stepsTitle: 'שלבים למטופלים',
      stepsSubtitle: 'רק 4 שלבים לפתור כל צורך בריאותי',
      steps: [
        { number: '01', title: 'התקשר לקו המטופל', description: 'התקשר 7000 0223 / 801 101 0364 ודבר עם הצוות המתמחה שלנו.' },
        { number: '02', title: 'ספר לנו מה אתה צריך', description: 'תור עם מומחה, בדיקה, גישה למרכז אבחון או בית מרקחת.' },
        { number: '03', title: 'אנחנו מטפלים בהכל', description: 'אנחנו קובעים תורים, בודקים הפניות, מוצאים את נקודת השירות הקרובה.' },
        { number: '04', title: 'אתה מקבל אישור', description: 'אנחנו מעדכנים אותך על התאריך, השעה והמיקום.' },
      ],
      benefitsTitle: 'סוף ללחץ',
      benefitsSubtitle: 'למה לבחור בקו המטופל',
      benefits: [
        { title: 'טלפון אחד לכל דבר', description: 'התקשר רק 7000 0223 / 801 101 0364 לכל צורך בריאותי.' },
        { title: 'חיסכון בזמן', description: 'ללא המתנה ושיחות אינסופיות.' },
        { title: '100% חינם', description: 'ללא תשלום למטופלים.' },
        { title: 'טיפול אישי', description: 'שירות מותאם אישית לכל מטופל.' },
        { title: 'עמידה ב-GDPR', description: 'הגנה מוחלטת על הנתונים האישיים שלך.' },
      ],
      ctaTitle: 'התקשר עכשיו',
      ctaSubtitle: 'זמינים כל יום לשרת אותך',
      compatible: 'תואם עם',
    },
    international: {
      back: 'חזרה',
      pageTitle: 'גישה בינלאומית לבריאות',
      subtitle: 'תיאום למטופלים המחפשים פתרונות מתמחים בחו"ל',
      description: 'אנו מבצעים תיאום מקצועי למטופלים המחפשים שירותים רפואיים מתקדמים, חוות דעת שנייה או טיפול במרכזי בריאות בינלאומיים מוכרים. בדיסקרטיות מוחלטת, הכוונה נכונה ותמיכה אישית בכל שלב.',
      missionQuote: '"Helping healthcare work better — פתרונות טיפול מתמחים מעבר לגבולות"',
      servicesTitle: 'שירותי תיאום בינלאומי',
      service1Title: 'תיאום מקרים רפואיים בחו"ל',
      service1Desc: 'תיאום מלא של טיפול במרכזים בינלאומיים מוכרים',
      service2Title: 'חוות דעת רפואית שנייה',
      service2Desc: 'גישה לרופאים מומחים ממוסדות מובילים',
      service3Title: 'תמיכה בתיק רפואי ותקשורת',
      service3Desc: 'ניהול מלא של מסמכים, תרגומים והתכתבויות',
      service4Title: 'ארגון תורים ונהלים',
      service4Desc: 'קביעת תורים, הכנת בדיקות, לוגיסטיקה',
      service5Title: 'הכוונה מותאמת אישית למטופלים',
      service5Desc: 'תמיכה אישית בכל שלב של התהליך',
      service6Title: 'Premium Patient Assistance',
      service6Desc: 'שירותי קונסיירז\' למטופלי VIP בדיסקרטיות מוחלטת',
      whoTitle: 'למי מיועד',
      patients: 'מטופלים',
      patientsDesc: 'אנחנו עוזרים לך לארגן גישה בטוחה למרכזי בריאות מתמחים, עם תמיכה מלאה מקפריסין ועד הגעתך',
      doctorsTitle: 'רופאים ומרפאות',
      doctorsDesc: 'אנו מטפלים בתיאום מנהלי ובינלאומי של מקרים מורכבים',
      hospitalsTitle: 'בתי חולים ומוסדות',
      hospitalsDesc: 'באמצעות שותפויות בינלאומיות עם ארגוני בריאות מוכרים, אנו מספקים גישה לשירותים מתמחים',
      ctaTitle: 'צריך טיפול מתמחה בחו"ל?',
      ctaDesc: 'גישה מהירה למומחים דרך משרד התיאום שלנו בקפריסין',
      ctaConsultation: 'בקש ייעוץ',
      ctaUpload: 'שלח דוחות רפואיים',
      ctaCall: 'דבר עם מתאם',
      faqTitle: 'שאלות נפוצות',
      faqSubtitle: 'גישה בינלאומית לבריאות',
      featuredPartnerLabel: 'מרכז רפואי שותף',
      holisticTagline: 'רפואה ברמה עולמית בישראל',
      holisticDescription: 'המרכז הרפואי שיבא בתל השומר מדורג באופן עקבי בין בתי החולים המובילים בעולם על פי Newsweek, ומוכר בינלאומית באונקולוגיה, קרדיולוגיה, השתלות, פוריות, נוירולוגיה וניתוחים מתקדמים. באמצעות Sheba Global ובתיאום עם J.House of Secretary, מטופלים בינלאומיים נהנים מגישה לרופאים מובילים, מחקר פורץ דרך וניהול תיק אישי מותאם ורב-לשוני — מהפנייה הראשונה ועד המעקב שלאחר הטיפול.',
      holisticTags: ['אונקולוגיה', 'קרדיולוגיה', 'השתלות', 'מטופלים בינלאומיים'],
      uploadModal: {
        title: 'העלאת מסמכים רפואיים',
        subtitle: 'שלחו את הדוחות הרפואיים והקבצים שלכם בבטחה',
        nameLabel: 'שם מלא',
        phoneLabel: 'טלפון',
        emailLabel: 'אימייל',
        messageLabel: 'הודעה (אופציונלי)',
        messagePlaceholder: 'תארו בקצרה את המקרה שלכם...',
        filesLabel: 'קבצים / תיקיות',
        filesHint: 'כל סוגי הקבצים מתקבלים • מקסימום 25MB בסך הכול',
        selectFiles: 'בחר קבצים',
        selectFolder: 'בחר תיקייה',
        clearFiles: 'נקה',
        gdprConsent: 'אני מסכים/ה לעיבוד הנתונים שלי בהתאם ל-GDPR למטרות בקשה זו.',
        submitBtn: 'שלח',
        cancelBtn: 'ביטול',
        sending: 'שולח...',
        successMsg: 'הבקשה שלך נשלחה בהצלחה. ניצור איתך קשר בקרוב.',
        errorMsg: 'השליחה נכשלה. אנא נסה שוב או צור איתנו קשר ישירות.',
        sizeError: 'הגודל הכולל של הקבצים חורג ממגבלת 25MB.',
        requiredError: 'אנא מלא את כל השדות הנדרשים.',
        gdprError: 'אנא קבל את הסכמת ה-GDPR כדי להמשיך.',
        rateLimitError: 'יותר מדי בקשות. אנא נסה שוב בעוד {minutes} דקות.',
        filesSelected: 'קבצים נבחרו',
      },
      faqItems: [
        { q: 'מהו שירות הגישה הבינלאומית לבריאות?', a: 'מדובר בשירות תיאום מקצועי למטופלים המחפשים חוות דעת שנייה, טיפול מתמחה או גישה למרכזי בריאות בינלאומיים מוכרים.' },
        { q: 'האם אתם מספקים ייעוץ רפואי?', a: 'לא. אנחנו לא מספקים אבחנה או טיפול רפואי. אנחנו מטפלים בתיאום מנהלי וארגוני בין מטופלים לספקי בריאות בינלאומיים.' },
        { q: 'איך מתחילים את התהליך?', a: 'פשוט צרו איתנו קשר ושלחו מידע בסיסי או תוצאות בדיקות זמינות כדי להעריך את דרך התיאום המתאימה ביותר.' },
        { q: 'האם אני צריך לנסוע לחו"ל?', a: 'לא תמיד. במקרים רבים ניתן לארגן תחילה חוות דעת שנייה או הערכה מרחוק.' },
        { q: 'האם אפשר לשלוח בדיקות אלקטרונית?', a: 'כן. ניתן לשלוח בדיקות, חוות דעת ומסמכים רפואיים באופן אלקטרוני ומאובטח.' },
        { q: 'באילו מדינות אתם יכולים לעזור?', a: 'בהתאם למקרה ולזמינות ארגונים שותפים, אנו מספקים גישה למרכזי בריאות בינלאומיים נבחרים.' },
        { q: 'כמה מהר יכולה בקשה להתקדם?', a: 'הזמן משתנה לפי מקרה, אך אנו נותנים עדיפות למקרים דחופים ורציניים עם תיאום מיידי.' },
        { q: 'כמה עולה השירות?', a: 'העלות תלויה בסוג הבקשה ובדרישות התיאום. מידע ניתן לפני תחילת התהליך.' },
        { q: 'איך אפשר להתחיל היום?', a: 'ניתן ליצור איתנו קשר בטלפון, וואטסאפ או דרך טופס יצירת הקשר באתר.' },
      ],
      footer: 'תיאום בריאות • גישה בינלאומית • תמיכה מקצועית',
    },
    cookieConsent: {
      title: 'אנחנו משתמשים בעוגיות',
      message: 'אתר זה משתמש בעוגיות כדי להבטיח לכם את החוויה הטובה ביותר. אנו משתמשים רק בעוגיות הנדרשות לפונקציונליות בסיסית ובניתוחים לשיפור השירותים שלנו.',
      learnMore: 'למידע נוסף',
      acceptAll: 'קבל הכל',
      necessaryOnly: 'רק הכרחיות',
      policyTitle: 'מדיניות עוגיות',
      section1Title: 'מהן עוגיות?',
      section1Body: 'עוגיות הן קבצי טקסט קטנים המאוחסנים במכשיר שלכם בעת ביקור באתר. הן עוזרות לאתר לפעול כראוי ומספקות חוויית גלישה טובה יותר.',
      section2Title: 'כיצד אנו משתמשים בעוגיות',
      section2Body: 'אנו משתמשים בעוגיות לפונקציונליות בסיסית של האתר (סשנים של התחברות, העדפות שפה) ולהבנה כיצד מבקרים מתקשרים עם האתר שלנו.',
      section3Title: 'סוגי העוגיות בהן אנו משתמשים',
      cookieType1: 'עוגיות חיוניות — נדרשות לפעולת האתר',
      cookieType2: 'עוגיות פונקציונליות — זוכרות את ההעדפות שלכם',
      cookieType3: 'עוגיות אנליטיות — עוזרות לנו להבין שימוש (אנונימי)',
      section4Title: 'הזכויות שלכם (GDPR)',
      section4Body: 'תוכלו לשנות או למשוך את הסכמתכם בכל עת על ידי ניקוי נתוני הדפדפן. בנוסף, יש לכם זכות לגשת, לתקן או למחוק את הנתונים האישיים שלכם בהתאם ל-GDPR של האיחוד האירופי.',
      contactTitle: 'צרו קשר',
    },
  },
};

const languageNames = {
  el: 'Ελληνικά',
  en: 'English',
  he: 'עברית',
  ru: 'Русский',
  ar: 'العربية',
  zh: '中文',
  fr: 'Français',
  es: 'Español',
};

const rtlLanguages = ['ar', 'he'];

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('el');

  // Sync <html lang="…"> so browsers apply language-specific typographic rules
  // (e.g. Greek CSS text-transform: uppercase strips tonos accents natively).
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      document.documentElement.dir = rtlLanguages.includes(language) ? 'rtl' : 'ltr';
    }
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    // Try active language first, fall back to English if the key is missing.
    const resolve = (langKey) => {
      let value = translations[langKey];
      for (const k of keys) {
        if (value && value[k] !== undefined && value[k] !== null) {
          value = value[k];
        } else {
          return undefined;
        }
      }
      return value;
    };
    const primary = resolve(language);
    if (primary !== undefined) return primary;
    const fallback = resolve('en');
    if (fallback !== undefined) return fallback;
    return key;
  }, [language]);

  const isRTL = rtlLanguages.includes(language);
  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languageNames, isRTL, dir }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
